import { isString, sortBy } from "lodash";
import Trie from "../../lib/trie";
import { FILE_TYPES_LIST } from "./mappings/file_types";
import Indicators from "./mappings/tc_indicators.json";

const TAG_DESCRIPTIONS_URL =
  "https://docs.reversinglabs.com/General/SpectraCore/core-tags/";
const INDICATOR_DESCRIPTIONS_URL =
  "https://docs.reversinglabs.com/General/SpectraCore/indicators/";

const endsWithEmptyValueRegex = /:\s+?$/;
export const getLatestValue = q => {
  const query = q.replace(endsWithEmptyValueRegex, ":");
  if (!query) return query;
  const lastSpaceIndex = query.lastIndexOf(" ");
  if (lastSpaceIndex === -1) return query;

  const lastQuotationIndex = query.lastIndexOf('"');

  if (lastQuotationIndex > -1) {
    const quoteBeforeLastIndex = query
      .substring(0, lastQuotationIndex - 1)
      .lastIndexOf('"');
    if (quoteBeforeLastIndex === -1 && lastSpaceIndex > lastQuotationIndex) {
      return query.substring(
        query.substring(0, lastQuotationIndex).lastIndexOf(" ") + 1
      );
    } else if (
      quoteBeforeLastIndex > -1 &&
      lastSpaceIndex > quoteBeforeLastIndex &&
      lastSpaceIndex < lastQuotationIndex
    ) {
      return query.substring(
        query.substring(0, quoteBeforeLastIndex).lastIndexOf(" ") + 1
      );
    }
  }

  const lastOpenBracketIndex = query.lastIndexOf("[");
  const lastCloseBracketIndex = query.lastIndexOf("]");
  if (
    lastOpenBracketIndex > -1 &&
    lastSpaceIndex > lastOpenBracketIndex &&
    (lastCloseBracketIndex === -1 ||
      lastOpenBracketIndex > lastCloseBracketIndex)
  ) {
    const lastSpaceBeforeOpenBracketIndex = query
      .substring(0, lastOpenBracketIndex)
      .lastIndexOf(" ");
    return query.substring(lastSpaceBeforeOpenBracketIndex + 1, query.length);
  }

  return query.substring(lastSpaceIndex + 1, query.length);
};

export const getLatestKeyword = query => {
  const q = query.replace(/\s{2,}/g);
  const queryElements = q.split(" ");
  if (queryElements.length >= 2) {
    if (queryElements.at(-2).endsWith(":")) return queryElements.at(-2);
  }
  return "";
};

export const hasBalancedBrackets = query => {
  const store = {
    "[": 0,
    "]": 0
  };
  query.split("").forEach(char => {
    if (char === "[" || char === "]") {
      store[char] += 1;
    }
  });
  return store["["] === store["]"];
};

const AFTER_COMPARISON_SUGGESTIONS = [
  {
    name: "NOT",
    value: " NOT "
  }
];

const COMPARISON_SUGGESTIONS = [
  {
    name: "OR",
    value: "OR",
    suggestions: AFTER_COMPARISON_SUGGESTIONS.map(({ name, value }) => ({
      name: `OR ${name}`,
      value: `OR ${value}`
    }))
  },
  {
    name: "AND",
    value: "AND",
    suggestions: AFTER_COMPARISON_SUGGESTIONS.map(({ name, value }) => ({
      name: `AND ${name}`,
      value: `AND ${value}`
    }))
  }
];

const OPERATORS = {
  EQ: "EQ",
  EQ_WILDCARD: "EQ_WILDCARD",
  IN: "IN",
  RANGE: "RANGE"
};

const { EQ, EQ_WILDCARD, IN, RANGE } = OPERATORS;

export const SEARCH_FIELD_ALIASES = {
  CLASS: "class:",
  AV_CLASS: "av-class:",
  THREAT_LEVEL: "threatlevel:",
  FACTOR: "factor:",
  IN_ALIAS: "in:",
  SHAREABLE: "shareable:",
  LS: "ls:",
  FS: "fs:",
  POSITIVES: "positives:",
  P: "p:",
  ANTIVIRUS: "antivirus:",
  FILETYPE: "filetype:",
  TYPE: "type:",
  FORMAT: "format:",
  ENGINES: "engines:",
  PDB: "pdb:",
  PETS: "pets:",
  DOC_DESCRIPTION: "doc-description:",
  DOC_PAGES: "doc-pages:",
  C2: "c2:",
  PE_SECTION_HASH: "pe-section-hash:",
  PE_OVERLAY_HASH: "pe-overlay-hash:",
  PE_RESOURCE_HASH: "pe-resource-hash:",
  MACHO_SEGMENT: "macho-segment:",
  ELF_SEGMENT_HASH: "elf-segment-hash:",
  MACHO_SEGMENT_HASH: "macho-segment-hash:",
  CERT_VALID_FROM: "cert-valid-from:",
  CERT_VALID_TO: "cert-valid-to:",
  APPID_CATEGORY: "appid-category:",
  APPID_AUTHOR: "appid-author:",
  ITW: "itw:",
  IMPORTS: "imports:",
  EXPORTS: "exports:",
  LA: "la:",
  IP: "ip:",
  NAME: "name:"
};

const AV_VENDORS_ALIAS = {
  AHNLAB: "ahnlab:",
  ANTIVIR: "antivir:",
  AVAST: "avast:",
  BITDEFENDER: "bitdefender:",
  CARBONBLACK: "carbonblack:",
  CLAMAV: "clamav:",
  CROWDSTRIKE: "crowdstrike:",
  DRWEB: "drweb:",
  ENDGAME: "endgame:",
  FFRI: "ffri:",
  FIREEYE: "fireeye:",
  FORTINET: "fortinet:",
  GDATA: "gdata:",
  IKARUS: "ikarus:",
  K7COMPUTING: "k7computing:",
  MALWAREBYTES: "malwarebytes:",
  MCAFEE: "mcafee:",
  MICROSOFT: "microsoft:",
  PANDA: "panda:",
  QUICKHEAL: "quickheal:",
  RISING: "rising:",
  SENTINELONE: "sentinelone:",
  SONICWALL: "sonicwall:",
  SOPHOS: "sophos:",
  SYMANTEC: "symantec:",
  TRENDMICRO: "trendmicro:",
  VARIST: "varist:",
  VBA32: "vba32:"
};

const AV_VENDORS = Object.entries(AV_VENDORS_ALIAS).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [`AV_${key}`]: `av-${value}`
  }),
  {}
);

export const SEARCH_FIELDS = {
  HASHES: "hashes:",
  SHA1: "sha1:",
  SHA256: "sha256:",
  MD5: "md5:",
  CLASSIFICATION: "classification:",
  AV_CLASSIFICATION: "av-classification:",
  AV_THREATLEVEL: "av-threatlevel:",
  RISKSCORE: "riskscore:",
  THREATNAME: "threatname:",
  AV_THREATNAME: "av-threatname:",
  SIZE: "size:",
  AVAILABLE: "available:",
  FIRSTSEEN: "firstseen:",
  LASTSEEN: "lastseen:",
  AV_COUNT: "av-count:",
  SAMPLETYPE: "sampletype:",
  FILENAME: "filename:",
  AV_DETECTION: "av-detection:",
  FILECOUNT: "filecount:",
  TAG: "tag:",
  IMPHASH: "imphash:",
  URI: "uri:",
  IPV4: "ipv4:",
  IPV6: "ipv6:",
  EMAIL: "email:",
  CERT_THUMBPRINT: "cert-thumbprint:",
  CERT_ISSUER_ORG: "cert-issuer-org:",
  CERT_ISSUER_UNIT: "cert-issuer-unit:",
  CERT_ISSUER_NAME: "cert-issuer-name:",
  CERT_ISSUER_COUNTRY: "cert-issuer-country:",
  CERT_SUBJECT_ORG: "cert-subject-org:",
  CERT_SUBJECT_UNIT: "cert-subject-unit:",
  CERT_SUBJECT_NAME: "cert-subject-name:",
  CERT_SUBJECT_COUNTRY: "cert-subject-country:",
  CERT_SERIAL: "cert-serial:",
  CERTIFICATE: "certificate:",
  CERTIFICATE_COUNTRY: "certificate-country:",
  PDB_PATH: "pdb-path:",
  PE_TIMESTAMP: "pe-timestamp:",
  PE_COMPANY_NAME: "pe-company-name:",
  PE_PRODUCT_NAME: "pe-product-name:",
  PE_ORIGINAL_NAME: "pe-original-name:",
  MUTEX: "mutex:",
  DOCUMENT_AUTHOR: "document-author:",
  DOCUMENT_SUBJECT: "document-subject:",
  DOCUMENT_TITLE: "document-title:",
  DOCUMENT_VERSION: "document-version:",
  DOCUMENT_PAGES: "document-pages:",
  DOCUMENT_DESCRIPTION: "document-description:",
  DOCUMENT: "document:",
  URI_STATIC: "uri-static:",
  URI_DYNAMIC: "uri-dynamic:",
  URI_CONFIG: "uri-config:",
  IPV4_STATIC: "ipv4-static:",
  IPV4_DYNAMIC: "ipv4-dynamic:",
  IPV6_STATIC: "ipv6-static:",
  EMAIL_STATIC: "email-static:",
  MUTEX_CONFIG: "mutex-config:",
  MUTEX_DYNAMIC: "mutex-dynamic:",
  PE_LANGUAGE: "pe-language:",
  PE_IMPORT: "pe-import:",
  PE_FUNCTION: "pe-function:",
  PE_EXPORT: "pe-export:",
  PE_RESOURCE: "pe-resource:",
  PE_SECTION_NAME: "pe-section-name:",
  PE_SECTION_COUNT: "pe-section-count:",
  PE_DESCRIPTION: "pe-description:",
  PE_COPYRIGHT: "pe-copyright:",
  PE_SECTION_SHA1: "pe-section-sha1:",
  PE_OVERLAY_SHA1: "pe-overlay-sha1:",
  PE_RESOURCE_SHA1: "pe-resource-sha1:",
  TAGGANT_NAME: "taggant-name:",
  SUBMISSIONS: "submissions:",
  LAST_ANALYSIS: "lastanalysis:",
  URI_SOURCE: "uri-source:",
  ANDROID_APP_NAME: "android-app-name:",
  ANDROID_PACKAGE: "android-package:",
  ANDROID_PERMISSION: "android-permission:",
  ANDROID_FEATURES: "android-features:",
  ANDROID_IMPORT: "android-import:",
  IOS_APP_NAME: "ios-app-name:",
  IOS_PACKAGE: "ios-package:",
  IOS_AUTHOR: "ios-author:",
  BROWSER_PACKAGE: "browser-package:",
  THIRD_PARTY_LIBRARY: "third-party-library:",
  THIRD_PARTY_PUBLISHER: "third-party-publisher:",
  DOTNET_MODULE_ID: "dotnet-module-id:",
  DOTNET_MODULE_NAME: "dotnet-module-name:",
  DOTNET_RESOURCE: "dotnet-resource:",
  DOTNET_ASSEMBLY: "dotnet-assembly:",
  DOTNET_TYPE_NAME: "dotnet-type-name:",
  DOTNET_METHOD_NAME: "dotnet-method-name:",
  DEX_CLASS_NAME: "dex-class-name:",
  DEX_METHOD_NAME: "dex-method-name:",
  EXIF: "exif:",
  TAG_YARA: "tag-yara:",
  TAG_USER: "tag-user:",
  ACTOR: "actor:",
  VERTICAL: "vertical:",
  ELF_SECTION_NAME: "elf-section-name:",
  ELF_SECTION_COUNT: "elf-section-count:",
  ELF_SYMBOL: "elf-symbol:",
  MACHO_SECTION_NAME: "macho-section-name:",
  MACHO_SECTION_COUNT: "macho-section-count:",
  MACHO_SEGMENT_NAME: "macho-segment-name:",
  MACHO_SEGMENT_COUNT: "macho-segment-count:",
  MACHO_SYMBOL: "macho-symbol:",
  MACHO_IMPORT: "macho-import:",
  EMAIL_FROM: "email-from:",
  EMAIL_TO: "email-to:",
  EMAIL_SUBJECT: "email-subject:",
  EMAIL_X_KEY: "email-x-key:",
  EMAIL_X_VALUE: "email-x-value:",
  DOMAIN: "domain:",
  SIMILAR_TO: "similar-to:",
  ATTACK_TACTIC: "attack-tactic:",
  ATTACK_TECHNIQUE: "attack-technique:",
  SUBMISSION_USER: "submission-user:",
  SUBMISSION_TIME: "submission-time:",
  PROCESSING_STATUS: "processing-status:",
  ELF_SEGMENT_SHA1: "elf-segment-sha1:",
  MACHO_SEGMENT_SHA1: "macho-segment-sha1:",
  DOTNET_PINVOKE_IMPORT: "dotnet-pinvoke-import:",
  DOTNET_PINVOKE_FUNCTION: "dotnet-pinvoke-function:",
  SIGNER_VALID_FROM: "signer-valid-from:",
  SIGNER_VALID_TO: "signer-valid-to:",
  TAGGANT_VALID_FROM: "taggant-valid-from:",
  TAGGANT_VALID_TO: "taggant-valid-to:",
  APPID_PRODUCT_TYPE: "appid-product-type:",
  APPID_PRODUCT_NAME: "appid-product-name:",
  APPID_COMPANY_NAME: "appid-company-name:",
  APPID_DESCRIPTION: "appid-description:",
  INDICATORS: "indicators:",
  SOFTWARE_PACKAGE: "software-package:",
  SOFTWARE_DESCRIPTION: "software-description:",
  SOFTWARE_AUTHOR: "software-author:",
  SOFTWARE: "software:",
  SECTION: "section:",
  SEGMENT: "segment:",
  EXPLOIT: "exploit:",
  ...SEARCH_FIELD_ALIASES,
  ...AV_VENDORS_ALIAS,
  ...AV_VENDORS
};

const {
  CLASS,
  AV_CLASS,
  THREAT_LEVEL,
  FACTOR,
  IN_ALIAS,
  SHAREABLE,
  LS,
  FS,
  POSITIVES,
  P,
  ANTIVIRUS,
  FILETYPE,
  TYPE,
  FORMAT,
  ENGINES,
  PDB,
  PETS,
  DOC_DESCRIPTION,
  DOC_PAGES,
  C2,
  PE_SECTION_HASH,
  PE_OVERLAY_HASH,
  PE_RESOURCE_HASH,
  MACHO_SEGMENT,
  ELF_SEGMENT_HASH,
  MACHO_SEGMENT_HASH,
  CERT_VALID_FROM,
  CERT_VALID_TO,
  APPID_CATEGORY,
  APPID_AUTHOR,
  ITW,
  IMPORTS,
  EXPORTS,
  LA,
  IP,
  NAME
} = SEARCH_FIELD_ALIASES;

const {
  HASHES,
  SHA1,
  SHA256,
  MD5,
  CLASSIFICATION,
  AV_CLASSIFICATION,
  AV_THREATLEVEL,
  RISKSCORE,
  THREATNAME,
  AV_THREATNAME,
  SIZE,
  AVAILABLE,
  FIRSTSEEN,
  LASTSEEN,
  AV_COUNT,
  SAMPLETYPE,
  FILENAME,
  AV_DETECTION,
  FILECOUNT,
  TAG,
  IMPHASH,
  URI,
  IPV4,
  IPV6,
  EMAIL,
  CERT_THUMBPRINT,
  CERT_ISSUER_ORG,
  CERT_ISSUER_UNIT,
  CERT_ISSUER_NAME,
  CERT_ISSUER_COUNTRY,
  CERT_SUBJECT_ORG,
  CERT_SUBJECT_UNIT,
  CERT_SUBJECT_NAME,
  CERT_SUBJECT_COUNTRY,
  CERT_SERIAL,
  PDB_PATH,
  PE_TIMESTAMP,
  PE_COMPANY_NAME,
  PE_PRODUCT_NAME,
  PE_ORIGINAL_NAME,
  MUTEX,
  DOCUMENT_AUTHOR,
  DOCUMENT_SUBJECT,
  DOCUMENT_TITLE,
  DOCUMENT_VERSION,
  DOCUMENT_DESCRIPTION,
  DOCUMENT_PAGES,
  URI_STATIC,
  URI_DYNAMIC,
  URI_CONFIG,
  IPV4_STATIC,
  IPV4_DYNAMIC,
  IPV6_STATIC,
  EMAIL_STATIC,
  MUTEX_CONFIG,
  MUTEX_DYNAMIC,
  PE_LANGUAGE,
  PE_IMPORT,
  PE_FUNCTION,
  PE_EXPORT,
  PE_RESOURCE,
  PE_SECTION_NAME,
  PE_SECTION_COUNT,
  PE_DESCRIPTION,
  PE_COPYRIGHT,
  PE_OVERLAY_SHA1,
  PE_RESOURCE_SHA1,
  PE_SECTION_SHA1,
  TAGGANT_NAME,
  SUBMISSIONS,
  LAST_ANALYSIS,
  URI_SOURCE,
  ANDROID_APP_NAME,
  ANDROID_PACKAGE,
  ANDROID_PERMISSION,
  ANDROID_FEATURES,
  ANDROID_IMPORT,
  IOS_APP_NAME,
  IOS_PACKAGE,
  IOS_AUTHOR,
  BROWSER_PACKAGE,
  THIRD_PARTY_LIBRARY,
  THIRD_PARTY_PUBLISHER,
  DOMAIN,
  DOTNET_MODULE_ID,
  DOTNET_MODULE_NAME,
  DOTNET_RESOURCE,
  DOTNET_ASSEMBLY,
  DOTNET_TYPE_NAME,
  DOTNET_METHOD_NAME,
  DEX_CLASS_NAME,
  DEX_METHOD_NAME,
  EXIF,
  TAG_YARA,
  TAG_USER,
  ACTOR,
  VERTICAL,
  ELF_SECTION_NAME,
  ELF_SECTION_COUNT,
  ELF_SYMBOL,
  MACHO_SECTION_NAME,
  MACHO_SECTION_COUNT,
  MACHO_SYMBOL,
  MACHO_SEGMENT_NAME,
  MACHO_IMPORT,
  MACHO_SEGMENT_COUNT,
  EMAIL_FROM,
  EMAIL_TO,
  EMAIL_SUBJECT,
  EMAIL_X_KEY,
  EMAIL_X_VALUE,
  SIMILAR_TO,
  ATTACK_TACTIC,
  ATTACK_TECHNIQUE,
  SUBMISSION_USER,
  SUBMISSION_TIME,
  PROCESSING_STATUS,
  ELF_SEGMENT_SHA1,
  MACHO_SEGMENT_SHA1,
  DOTNET_PINVOKE_IMPORT,
  DOTNET_PINVOKE_FUNCTION,
  SIGNER_VALID_FROM,
  SIGNER_VALID_TO,
  TAGGANT_VALID_FROM,
  TAGGANT_VALID_TO,
  APPID_PRODUCT_TYPE,
  APPID_PRODUCT_NAME,
  APPID_COMPANY_NAME,
  APPID_DESCRIPTION,
  INDICATORS,
  SOFTWARE_PACKAGE,
  SOFTWARE_DESCRIPTION,
  SOFTWARE_AUTHOR,
  CERTIFICATE,
  CERTIFICATE_COUNTRY,
  SECTION,
  SEGMENT,
  SOFTWARE,
  DOCUMENT,
  EXPLOIT
} = SEARCH_FIELDS;

export const TAGS_LIST = [
  "access-control-information",
  "account-settings-tamper",
  "android-10",
  "android-11",
  "android-cupcake",
  "android-donut",
  "android-eclair",
  "android-froyo",
  "android-gingerbread",
  "android-honeycomb",
  "android-ice-cream-sandwich",
  "android-jelly-bean",
  "android-kitkat",
  "android-lollipop",
  "android-marshmallow",
  "android-nougat",
  "android-oreo",
  "android-pie",
  "anonymous-email",
  "antidebugging",
  "antidumping",
  "antiemulation",
  "antisandbox",
  "antitracing",
  "antivirus",
  "arch-arm",
  "arch-arm-64",
  "arch-mips",
  "arch-powerpc",
  "arch-sparc",
  "arch-x86",
  "arch-x86-64",
  "autorun",
  "av-disable",
  "av-impersonate",
  "av-service-detect",
  "av-tamper",
  "backdoor",
  "backup-tamper",
  "behavior",
  "bidirectional-text",
  "bitlocker-tamper",
  "brave-reference",
  "c2",
  "capability-advertising",
  "capability-bluetooth",
  "capability-camera",
  "capability-cryptography",
  "capability-deprecated",
  "capability-embeds",
  "capability-execution",
  "capability-filesystem",
  "capability-identification",
  "capability-microphone",
  "capability-networking",
  "capability-nfc",
  "capability-scripting",
  "capability-security",
  "capability-social",
  "capability-undocumented",
  "capability-vpn",
  "capability-wallet",
  "cert-appendix",
  "cert-bad-checksum",
  "cert-bad-timestamp",
  "cert-blacklisted",
  "cert-cross-signed",
  "cert-dual-signed",
  "cert-expired",
  "cert-impersonate",
  "cert-invalid",
  "cert-malformed",
  "cert-pagehashes-bad-checksum",
  "cert-revoked",
  "cert-revoked-aa-compromise",
  "cert-revoked-affiliation-changed",
  "cert-revoked-ca-compromise",
  "cert-revoked-cert-hold",
  "cert-revoked-cessation-of-operation",
  "cert-revoked-key-compromise",
  "cert-revoked-privilege-withdrawn",
  "cert-revoked-remove-from-crl",
  "cert-revoked-superseded",
  "cert-revoked-unspecified",
  "cert-self-signed",
  "cert-signed",
  "cert-signed-after-expiration",
  "cert-signed-after-revocation",
  "cert-signed-after-valid-time",
  "cert-signed-before-issuing",
  "cert-timestamped",
  "cert-timestamped-revoked",
  "cert-timestamped-untrusted",
  "cert-untrusted",
  "cert-weak-crypto",
  "cert-weak-crypto-digest",
  "cert-weak-crypto-key",
  "cert-whitelisted",
  "chrome-reference",
  "chrome-tamper",
  "chromium-reference",
  "chromium-tamper",
  "cloud",
  "codeview",
  "compression-aplib",
  "compression-asdpack",
  "compression-aspack",
  "compression-brieflz",
  "compression-brotli",
  "compression-bzip2",
  "compression-deflate",
  "compression-dicky",
  "compression-ffce",
  "compression-gipfeli",
  "compression-gzip",
  "compression-inflate",
  "compression-jcalg",
  "compression-lz4",
  "compression-lzbrs",
  "compression-lzfse",
  "compression-lzhuf",
  "compression-lzma",
  "compression-lzmat",
  "compression-lznt",
  "compression-lzo",
  "compression-lzrw",
  "compression-lzss",
  "compression-ncompress42",
  "compression-neolite",
  "compression-nrv",
  "compression-pithy",
  "compression-pkzip",
  "compression-pucrunch",
  "compression-snappy",
  "compression-unlzx",
  "compression-unrarlib",
  "compression-zip",
  "compression-zlib",
  "compression-zstd",
  "contains-api-key",
  "contains-archive",
  "contains-document",
  "contains-elf",
  "contains-key-secret-pair",
  "contains-macho",
  "contains-pe",
  "contains-private-key-encrypted",
  "contains-private-key-plaintext",
  "contains-private-ssh-key-encrypted",
  "contains-private-ssh-key-plaintext",
  "contains-script",
  "contains-token",
  "contains-webhook",
  "crypto-acss",
  "crypto-adler-crc32",
  "crypto-base32",
  "crypto-base64",
  "crypto-base64url",
  "crypto-bcrypt",
  "crypto-bhencode",
  "crypto-blake",
  "crypto-blowfish",
  "crypto-bmw512",
  "crypto-botan",
  "crypto-camellia",
  "crypto-cast",
  "crypto-cast256",
  "crypto-clefia",
  "crypto-collision",
  "crypto-crc32",
  "crypto-cryptlib",
  "crypto-cryptopp",
  "crypto-des",
  "crypto-desx",
  "crypto-dsa",
  "crypto-ecc",
  "crypto-frog",
  "crypto-gnupg",
  "crypto-gnutls",
  "crypto-gost",
  "crypto-haval",
  "crypto-hmac",
  "crypto-ike",
  "crypto-kasumi",
  "crypto-keccak",
  "crypto-mars",
  "crypto-md2",
  "crypto-md4",
  "crypto-md5",
  "crypto-md5mac",
  "crypto-misty1",
  "crypto-misty2",
  "crypto-nacl",
  "crypto-nettle",
  "crypto-noekeon",
  "crypto-nss",
  "crypto-nush",
  "crypto-openbsd-base64",
  "crypto-openssl",
  "crypto-pbkdf2",
  "crypto-pkcs",
  "crypto-rawdes",
  "crypto-rc2",
  "crypto-rc4",
  "crypto-rijndael",
  "crypto-ripemd128",
  "crypto-ripemd160",
  "crypto-ripemd256",
  "crypto-ripemd320",
  "crypto-rsa",
  "crypto-rtss",
  "crypto-safer",
  "crypto-salsa20",
  "crypto-seed",
  "crypto-serpent",
  "crypto-sha1",
  "crypto-sha224",
  "crypto-sha256",
  "crypto-sha384",
  "crypto-sha512",
  "crypto-shark",
  "crypto-siphash",
  "crypto-skein",
  "crypto-skipjack",
  "crypto-sms4",
  "crypto-sosemanuk",
  "crypto-square",
  "crypto-tiger",
  "crypto-tripledes",
  "crypto-turing",
  "crypto-twofish",
  "crypto-unicorn",
  "crypto-uuencode",
  "crypto-wake",
  "crypto-whirlpool",
  "crypto-x509",
  "crypto-xxencode",
  "cryptocurrency",
  "cui",
  "custom-packed",
  "data-exfiltration",
  "dde",
  "deceptive-link",
  "desktop",
  "disposable-email",
  "dns-tamper",
  "dns-use",
  "downloader",
  "edge-reference",
  "email-anonymous-provider",
  "email-attachment",
  "email-body-html",
  "email-body-plain",
  "email-body-rtf",
  "email-deceptive-extension",
  "email-deceptive-sender",
  "email-dkim-fail",
  "email-dmarc-fail",
  "email-envelopefrom-mismatch",
  "email-hidden-text",
  "email-impersonation",
  "email-outlook",
  "email-pattern",
  "email-pgp",
  "email-receivedtime-mismatch",
  "email-replyto-mismatch",
  "email-returnpath-mismatch",
  "email-sender-mismatch",
  "email-sensitive-topic",
  "email-signature-impersonation",
  "email-smime",
  "email-spf-fail",
  "email-subject-phishing",
  "email-subject-spam",
  "email-thunderbird",
  "email-urgency",
  "encrypted",
  "entropy-high",
  "entropy-zero",
  "exif",
  "exploit",
  "fake-signature",
  "file-download",
  "file-overwrite",
  "file-upload",
  "firefox-reference",
  "firefox-tamper",
  "firewall-tamper",
  "font-embedded",
  "force-integrity",
  "format-bad-checksum",
  "format-bad-password",
  "format-unsupported",
  "ftp-use",
  "geotagging",
  "graylisting",
  "gui",
  "guid-activex-killbit",
  "hierarchy-analyzer",
  "hosts-modifier",
  "html-canvas",
  "html-download",
  "html-form",
  "html-frame",
  "html-image",
  "html-input",
  "html-local-link",
  "html-object",
  "html-password",
  "html-popup",
  "html-tracking",
  "html-wsffile",
  "im-skype",
  "image-analyzer",
  "image-corrupt",
  "image-malformed",
  "image-segment-duplicate",
  "image-segment-unexpected-location",
  "image-segment-unknown",
  "impersonate-native",
  "import-elimination",
  "import-redirection",
  "indicator-anomaly",
  "indicator-autostart",
  "indicator-behavior",
  "indicator-disable",
  "indicator-document",
  "indicator-evasion",
  "indicator-execution",
  "indicator-exploit",
  "indicator-family",
  "indicator-file",
  "indicator-flow",
  "indicator-macro",
  "indicator-memory",
  "indicator-monitor",
  "indicator-network",
  "indicator-packer",
  "indicator-payload",
  "indicator-permissions",
  "indicator-registry",
  "indicator-search",
  "indicator-settings",
  "indicator-signature",
  "indicator-steal",
  "indicator-stealth",
  "installer",
  "installer-plugin",
  "internet-explorer-reference",
  "internet-explorer-tamper",
  "irc-use",
  "keylogger",
  "library-ad",
  "library-analytics",
  "library-audio",
  "library-browser",
  "library-cloud",
  "library-compression",
  "library-crypto",
  "library-database",
  "library-development",
  "library-driver",
  "library-educational",
  "library-email",
  "library-entertainment",
  "library-gaming",
  "library-graphics",
  "library-messaging",
  "library-multimedia",
  "library-networking",
  "library-productivity",
  "library-security",
  "library-social",
  "library-utility",
  "library-virtualization",
  "linguist",
  "log-tamper",
  "lolbin",
  "loldriver",
  "machine-learning",
  "ml-model",
  "mobile",
  "mobile-custom-permissions",
  "mobile-data-access",
  "mobile-deprecated",
  "mobile-gps",
  "mobile-infostealer",
  "mobile-logging",
  "mobile-settings",
  "mobile-sms",
  "mobile-telco",
  "mobile-voicemail",
  "netntlm-hash-leak",
  "netscape-reference",
  "netscape-tamper",
  "network-settings-tamper",
  "nfs-tamper",
  "ng-antivirus",
  "nsis-table-invalid-offset",
  "nsis-table-invalid-size",
  "ntfs-alternate-data-stream",
  "obfuscated",
  "opera-reference",
  "opera-tamper",
  "overlay",
  "packed",
  "password",
  "pe-bad-checksum",
  "pe-compression",
  "pe-encryption",
  "pe-encryption-rc4",
  "pe-encryption-tea",
  "platform-unix",
  "platform-windows",
  "plugin",
  "polymorphic",
  "pos",
  "privacy-intrusion",
  "privilege-escalation",
  "probably-packed",
  "process-injection",
  "process-termination",
  "protection-aslr",
  "protection-cet",
  "protection-cfg",
  "protection-cpy",
  "protection-dep",
  "protection-ehc",
  "protection-mpx",
  "protection-ret",
  "protection-rfg",
  "protection-scg",
  "protection-sdl",
  "protection-seh",
  "protection-stack",
  "protection-xfg",
  "proxy",
  "quarantine-malicious-content",
  "quarantine-manual",
  "quarantine-threat-metadata",
  "ransomware",
  "ransomware-artifact",
  "ransomware-encrypted",
  "registry-tamper",
  "remote-target-mhtml",
  "remove-ep",
  "remove-header",
  "reproducible-build",
  "ricc",
  "rich-header",
  "safari-reference",
  "safari-tamper",
  "sandbox",
  "script",
  "seamonkey-reference",
  "security-settings-tamper",
  "service-disable",
  "sfx",
  "signature",
  "smb-tamper",
  "sql-query",
  "ssh-key",
  "startup-tamper",
  "stego",
  "stego-compressed",
  "stego-embedded",
  "stego-encoded",
  "stego-encrypted",
  "storage-settings-tamper",
  "storage-tamper",
  "string-callto",
  "string-file",
  "string-ftp",
  "string-h323",
  "string-http",
  "string-https",
  "string-mailto",
  "string-scp",
  "string-sftp",
  "string-sip",
  "string-ssh",
  "string-telnet",
  "string-webcal",
  "taggant",
  "tamper-protection",
  "threat-hunting-npm",
  "threat-hunting-pypi",
  "tool-hacktool",
  "tool-steganography",
  "uac-bypass",
  "uefi",
  "uninstaller",
  "unsupported-application",
  "update-disable",
  "updater",
  "uri-banking-website",
  "uri-coinmining-domain",
  "uri-credentials",
  "uri-deceptive-file",
  "uri-domain-blacklisted",
  "uri-domain-homoglyph",
  "uri-domain-phishtest",
  "uri-domain-punycode",
  "uri-domain-spoofed",
  "uri-domain-typosquat",
  "uri-dynamic-dns",
  "uri-hostname-length",
  "uri-interesting-file",
  "uri-ip-address",
  "uri-malicious-redirect",
  "uri-malware-regex",
  "uri-onion-website",
  "uri-open-redirect",
  "uri-path-length",
  "uri-path-spoofed",
  "uri-placeholder",
  "uri-placeholder-known",
  "uri-restricted",
  "uri-sanctioned-eu",
  "uri-sanctioned-us",
  "uri-security-website",
  "uri-shortened",
  "uri-subdomain-count",
  "uri-suspicious-path",
  "uri-suspicious-port",
  "uri-suspicious-query",
  "uri-suspicious-tld",
  "version-control-artifact",
  "version-info",
  "virtualization-settings-tamper",
  "vivaldi-reference",
  "vpn-tamper",
  "vpn-use",
  "vulnerable-with-cve",
  "vulnerable-without-cve",
  "waterfox-reference",
  "web-request",
  "wmi-use",
  "xbox",
  "yandex-reference",
  "yara",
  "crypto-sha1mac",
  "crypto-sha224mac",
  "crypto-sha256mac",
  "crypto-sha384mac",
  "crypto-sha512mac",
  "crypto-ripemd160mac",
  "crypto-sha3-224mac",
  "crypto-sha3-256mac",
  "crypto-sha3-384mac",
  "crypto-sha3-512mac",
];

const buildOperators = operators =>
  operators.reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: true
    }),
    {}
  );

const getValuesFromQuery = (query, delimiter) => {
  const openBracketIndex = query.indexOf("[");
  if (openBracketIndex === -1) return "";
  const closeBracketIndex = query.indexOf("]");
  return query
    .substring(
      openBracketIndex + 1,
      closeBracketIndex > openBracketIndex ? closeBracketIndex : query.length
    )
    .split(delimiter)
    .map(s => s.trim())
    .filter(s => !!s);
};

class SearchSuggestion {
  constructor(prefix, options = {}) {
    const { values, operators, formatValue, helpText, aliases, isAlias } =
      options;

    this.prefix = prefix;
    this.values = values || [];
    if (formatValue) {
      this.values = this.values.map(formatValue);
    }
    this.operators = buildOperators(operators || []);
    this.helpText = helpText || {};
    this.aliases = aliases || [];
    this.isAlias = isAlias;

    this.getSuggestions = this.getSuggestions.bind(this);
  }

  getSuggestions() {
    const operatorValues = [];

    if (this.operators.IN) {
      operatorValues.push({
        operator: IN,
        name: `${this.prefix}[\x0cvalue1, value2, ...]`,
        value: `${this.prefix}[`,
        helpText: this.helpText.IN || "",
        isAlias: this.isAlias,
        filter: query => query.indexOf(" ") === -1 || query.indexOf(",") > -1,
        getSuggestion: query => {
          const defaultValues = ["value1", "value2"];
          const queryValues = getValuesFromQuery(query, ",");
          const suggestionValues = [];

          if (this.values.length && queryValues.length) {
            suggestionValues.push(
              ...queryValues.slice(0, queryValues.length - 1)
            );
            const queryValue = queryValues[queryValues.length - 1];
            this.values.forEach(value => {
              if (
                value
                  .toString()
                  .toLowerCase()
                  .startsWith(queryValue.toLowerCase())
              ) {
                suggestionValues.push(value);
              }
            });
          } else {
            suggestionValues.push(...queryValues);
          }

          const mergedValues = Object.values({
            ...defaultValues,
            ...suggestionValues
          });
          const totalValues = this.values.length || Infinity;
          const suggestionNames = [
            ...mergedValues,
            mergedValues.length !== totalValues ? "..." : ""
          ];
          return {
            name: `${this.prefix}[${suggestionNames.join(", ")}]`,
            value: `${this.prefix}[${suggestionValues
              .slice(0, queryValues.length)
              .join(", ")}${totalValues <= suggestionValues.length ? "]" : ""}`
          };
        }
      });
    }

    if (this.operators.RANGE) {
      operatorValues.push({
        operator: RANGE,
        name: `${this.prefix}[\x0cN TO N]`,
        value: `${this.prefix}[`,
        helpText: this.helpText.RANGE || "",
        isAlias: this.isAlias,
        filter: query => query.indexOf(",") === -1,
        getSuggestion: query => {
          const defaultValues = ["N", "TO", "N"];
          const queryValues = getValuesFromQuery(query, " ");
          const suggestionValues = [];

          if (this.values.length && queryValues) {
            queryValues.forEach(queryValue => {
              this.values.forEach(value => {
                if (
                  value
                    .toString()
                    .toLowerCase()
                    .startsWith(queryValue.toLowerCase())
                ) {
                  suggestionValues.push(value);
                  if (suggestionValues.length === 1) {
                    suggestionValues.push("TO");
                  }
                }
              });
            });
          } else {
            suggestionValues.push(...queryValues);
            if (suggestionValues.length === 1) {
              suggestionValues.push("TO");
            }
          }
          const mergedValues = Object.values({
            ...defaultValues,
            ...suggestionValues
          });
          const totalValues = 3;
          return {
            name: `${this.prefix}[${mergedValues.join(" ")}]`,
            value: `${this.prefix}[${suggestionValues.join(" ")}${
              totalValues === suggestionValues.length ? "]" : ""
            }`
          };
        }
      });
    }

    const aliasValues = this.aliases.map(alias =>
      new SearchSuggestion(alias, {
        operators: Object.keys(this.operators),
        values: this.values,
        helpText: this.helpText,
        isAlias: true
      }).getSuggestions()
    );

    return {
      name: this.prefix,
      operators: this.operators,
      values: this.values,
      value: this.prefix,
      helpText: this.helpText.EQ || "",
      aliases: this.aliases,
      isAlias: this.isAlias,
      suggestions: [
        ...operatorValues,
        ...aliasValues,
        ...this.values.map(value => {
          const valueIsString = isString(value);
          const name = valueIsString
            ? `${this.prefix}${value}`
            : `${this.prefix}${value.value}`;
          const helpText = valueIsString ? "" : value.helpText;

          return new SearchSuggestion(name, {
            helpText: { EQ: helpText },
            isAlias: this.isAlias
          }).getSuggestions();
        })
      ]
    };
  }
}

export const SEARCH_FIELD_SUGGESTIONS = sortBy(
  [
    new SearchSuggestion(HASHES, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find a file by it&apos;s SHA1, SHA256 or MD5 hash.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            Example:{" "}
            <code>hashes:13fabd46cebcf323ef21be1825141b1642cd7dba</code>
          </>
        ),
        IN: (
          <>
            Find files by any of listed SHA1, SHA256, MD5 hashes.
            <br />
            Different types of hashes can be combined in one search.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            Example:{" "}
            <code>hashes:[fc45f04c6118(...), 843f0247651ce848(...)]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SHA1, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find a file by its SHA1 hash.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            Example: <code>sha1:ea728abe26bac161e110970051e1561fd51db93b</code>
          </>
        ),
        IN: (
          <>
            Find files with any of the listed SHA1 hashes.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            The list can contain at most 50 SHA1 hashes.
            <br />
            Example: <code>sha1:[fc45f04c6118(...),1782723c9246e(...)]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SHA256, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find a file by its SHA256 hash.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            Example: <code>sha256: 22d96c1aa22c1753(...)</code>
          </>
        ),
        IN: (
          <>
            Find files with any of the listed SHA256 hashes.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            The list can contain at most 32 SHA256 hashes.
            <br />
            Example:{" "}
            <code>sha256:[843f0247651ce848(...), c629b7b8ff6(...)]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(MD5, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find a file by its MD5 hash.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            Example: <code>md5:8f37315e622b3f6e1e2d7c983e764c3d</code>
          </>
        ),
        IN: (
          <>
            Find files with any of the listed MD5 hashes.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            The list can contain at most 59 MD5 hashes.
            <br />
            Example: <code>md5:[081425da(...), 85a300e9(...)]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CLASSIFICATION, {
      values: ["unknown", "goodware", "suspicious", "malicious"],
      operators: [EQ, IN],
      aliases: [CLASS],
      helpText: {
        EQ: (
          <>
            Possible values are:
            <code>unknown</code>,<code>goodware</code>,<code>suspicious</code>,
            <code>malicious</code>,<br />
            <code>known DEPRECATED</code>.<br />
            Example: <code>{CLASSIFICATION}goodware</code>
          </>
        ),
        IN: (
          <>
            Possible values are:
            <code>unknown</code>,<code>goodware</code>,<code>suspicious</code>,
            <code>malicious</code>,<br />
            <code>known DEPRECATED</code>.<br />
            Example: <code>{CLASSIFICATION}[suspicious, malicious]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(AV_CLASSIFICATION, {
      values: ["unknown", "goodware", "suspicious", "malicious"],
      operators: [EQ, IN],
      aliases: [AV_CLASS],
      helpText: {
        EQ: (
          <>
            Possible values are:
            <code>unknown</code>,<code>goodware</code>,<code>suspicious</code>,
            <code>malicious</code>,<br />
            Example: <code>{AV_CLASSIFICATION}goodware</code>
          </>
        ),
        IN: (
          <>
            Possible values are:
            <code>unknown</code>,<code>goodware</code>,<code>suspicious</code>,
            <code>malicious</code>,<br />
            Example: <code>{AV_CLASSIFICATION}[suspicious, malicious]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(AV_THREATLEVEL, {
      aliases: [THREAT_LEVEL],
      operators: [EQ, RANGE, IN],
      values: ["6", "7", "8", "9", "10"],
      helpText: {
        EQ: (
          <>
            Search for samples with specified antivirus threat level.
            <br />
            Possible values are:
            <code>6, 7, 8, 9, 10</code>.<br />
            Example: <code>{AV_THREATLEVEL}7</code>
          </>
        ),
        IN: (
          <>
            Search for files with antivirus threat level matching any value in
            the list.
            <br />
            Possible values are:
            <code>6, 7, 8, 9, 10</code>.<br />
            Example: <code>{AV_THREATLEVEL}[7,8]</code>
          </>
        ),
        RANGE: (
          <>
            Search for greater/less than values or the exact range of values.
            <br />
            Possible values are:
            <code>6, 7, 8, 9, 10</code>.<br />
            Examples: <code>{AV_THREATLEVEL}[7 TO *]</code>,{" "}
            <code>{AV_THREATLEVEL}7+</code>,<code>av-threatlevel:[7 TO 9]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(RISKSCORE, {
      operators: [EQ, RANGE, IN],
      values: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
      aliases: [FACTOR],
      helpText: {
        EQ: (
          <>
            Search for files with specified Riskscore.
            <br />
            Possible values are:
            <code>0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10</code>.<br />
            Example: <code>{RISKSCORE}5</code>
          </>
        ),
        IN: (
          <>
            Search for files with Riskscore matching any value in the list.
            <br />
            Possible values are:
            <code>0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10</code>.<br />
            Example: <code>{RISKSCORE}[1,2]</code>
          </>
        ),
        RANGE: (
          <>
            Search for greater/less than values or the exact range of values.
            <br />
            Possible values are:
            <code>0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10</code>
            .<br /> Examples: <code>{RISKSCORE}[* TO 3]</code>,
            <code>{RISKSCORE}3-</code>,<code>riskscore:[2 TO 4]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(THREATNAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for the exact threat name or use wildcards.
            <br />
            Examples: <code>threatname: Win32.pua.bitminer</code>,<br />
            <code>{THREATNAME}*coinminer</code>
          </>
        ),
        IN: (
          <>
            Search for any threat name in the list.
            <br />
            Example:{" "}
            <code>
              {THREATNAME}[&quot;WIN32.PUA.casino eldorado&quot;, *crytex]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(AV_THREATNAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for the exact antivirus threat name or use wildcards.
            <br />
            Examples: <code>av-threatname: Win32.pua.bitminer</code>,<br />
            <code>{AV_THREATNAME}*coinminer</code>
          </>
        ),
        IN: (
          <>
            Search for any antivirus threat name in the list.
            <br />
            Example:{" "}
            <code>
              {AV_THREATNAME}[&quot;WIN32.PUA.casino eldorado&quot;,*crytex]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SIZE, {
      operators: [EQ, RANGE],
      helpText: {
        EQ: (
          <>
            Search for files by size (in bytes).
            <br />
            Example: <code>{SIZE}50000</code>
          </>
        ),
        RANGE: (
          <>
            Search for smaller/bigger files (in bytes).
            <br />
            Example: <code>{SIZE}[* TO 50000]</code>, <code>{SIZE}50000-</code>
            <br />
            Search for files with sizes within the range (in bytes).
            <br />
            Example: <code>{SIZE}[100000 TO 200000]</code>,{" "}
            <code>{SIZE}[4KiB TO 5MiB]</code>, <code>{SIZE}[4KB TO 5MB]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(AVAILABLE, {
      operators: [EQ],
      aliases: [IN_ALIAS, SHAREABLE],
      helpText: {
        EQ: (
          <>
            Search for files by download availability.
            <br />
            Possible values: <code>true, false</code> <br />
            Example: <code>{AVAILABLE}true</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(FIRSTSEEN, {
      operators: [EQ],
      aliases: [FS],
      helpText: {
        EQ: (
          <>
            Search for files by date when they were first received by Spectra
            Intelligence
            <br />
            or first uploaded and analyzed on Spectra Analyze.
            <br />
            Example:{" "}
            <code>
              {FIRSTSEEN}[2017-12-01T11:36:59Z TO 2018-03-06T11:36:59Z]
            </code>
            ,<code>{FIRSTSEEN}2017-12-01T11:36:59Z+</code>,
            <code>{FIRSTSEEN}1d+</code>, <code>{FIRSTSEEN}1y-</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(LASTSEEN, {
      operators: [EQ],
      aliases: [LS],
      helpText: {
        EQ: (
          <>
            Search for files by date when they were last received by Spectra
            Intelligence
            <br />
            or last analyzed on Spectra Analyze.
            <br />
            Example:{" "}
            <code>
              {LASTSEEN}[2018-02-01T12:05:41Z TO 2018-03-06T12:05:41Z]
            </code>
            ,<code>{LASTSEEN}2018-03-06T12:05:41Z-</code>,
            <code>{LASTSEEN}2m+</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(AV_COUNT, {
      operators: [EQ, IN, RANGE],
      aliases: [POSITIVES, P, ANTIVIRUS],
      helpText: {
        RANGE: (
          <>
            Search for files with AV scan matches within a range.
            <br />
            Example: <code>{AV_COUNT}[1 TO 3]</code>
            <br />
            Search for files with greater/smaller number of AV scan matches.
            <br />
            Example: <code>{AV_COUNT}[10 TO *]</code>, <code>{AV_COUNT}1+</code>
            , <code>{AV_COUNT}10-</code>
          </>
        ),
        IN: (
          <>
            Search for files with any number of AV scan matches in the list.
            <br />
            Example: <code>{AV_COUNT}[1, 2, 10]</code>
          </>
        ),
        EQ: (
          <>
            Search for files with the exact number of AV scan matches.
            <br />
            Example: <code>{AV_COUNT}5</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SAMPLETYPE, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [FILETYPE, TYPE, FORMAT],
      values: FILE_TYPES_LIST,
      helpText: {
        EQ: (
          <>
            Search for files by exact type or use wildcards.
            <br />
            File types are provided by Spectra Core analysis.
            <br />
            Example: <code>{SAMPLETYPE}document*</code>
          </>
        ),
        IN: (
          <>
            Search for any file type in the list.
            <br />
            File types are provided by Spectra Core analysis.
            <br />
            Example: <code>{SAMPLETYPE}[elf*, macho*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(FILENAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [NAME],
      helpText: {
        EQ: (
          <>
            Search for files by exact filename or use wildcards.
            <br />
            Predicted filename can be used instead of the original filename.
            <br />
            Example: <code>{FILENAME}notepad.exe</code>
          </>
        ),
        IN: (
          <>
            Search for any filename or part of the filename in the list.
            <br />
            Predicted filenames can be used instead of the original filenames.
            <br />
            Example: <code>{FILENAME}[*.pdf, *registry*, Firefox]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(AV_DETECTION, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [ENGINES],
      helpText: {
        EQ: (
          <>
            Search for files by exact AV detection string or use wildcards.
            <br />
            Example: <code>{AV_DETECTION}*petya*</code>
          </>
        ),
        IN: (
          <>
            Search for any AV detection string in the list.
            <br />
            Example: <code>{AV_DETECTION}[W32.Duqu, *Vitro]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(FILECOUNT, {
      operators: [EQ, RANGE],
      helpText: {
        EQ: (
          <>
            Search for files by exact number of unpacked files they contain (if
            any).
            <br />
            Example: <code>{FILECOUNT}2</code>
          </>
        ),
        RANGE: (
          <>
            Search for files by number of unpacked files they contain (if any).
            <br />
            The number can be within a range or a greater/smaller than value.
            <br />
            Example: <code>{FILECOUNT}[10 TO 20]</code>,{" "}
            <code>filecount:[* TO 10]</code>, <code>filecount:10-</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(TAG, {
      operators: [EQ, EQ_WILDCARD, IN],
      values: TAGS_LIST,
      helpText: {
        EQ: (
          <p>
            Search for files tagged with an exact metadata tag.
            <br />
            Tags are generated by Spectra Core based on file metadata
            properties.
            <br />
            The full list of Spectra Core tags and their descriptions can be
            found{" "}
            <a
              href={TAG_DESCRIPTIONS_URL}
              onClick={e => e.stopPropagation()}
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>
            .
            <br />
            Examples: <code>{TAG}capability-execution</code>,{" "}
            <code>tag:indicator-disable</code>
          </p>
        ),
        IN: (
          <>
            Search for files tagged with any of the Spectra Core metadata tags
            in the list.
            <br />
            Example: <code>{TAG}[cryptography,capability-filesystem,gui]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(IMPHASH, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for PE files only by their imphash value.
            <br />
            Example: <code>{IMPHASH}f34d5f2d4577ed6d9ceec516c1f5a744</code>
          </>
        ),
        IN: (
          <>
            Search for PE files with any of the imphash values in the list.
            <br />
            Example: <br />
            <code>
              {IMPHASH}[37053b320e3ad403274aa9cdf252033c,
              bc70c4fa605f17c85050b7c7b6d42e44]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(URI, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by URI strings (IPs, URLs, domains).
            <br />
            Provide an exact URI or use wildcards.
            <br />
            Example: <code>{URI}mozilla.org*</code>
          </>
        ),
        IN: (
          <>
            Search for files with any of the URI strings in the list.
            <br />
            Provide exact URIs (IPs, URLs, domains) or use wildcards.
            <br />
            Example: <code>{URI}[*.tor,*.onion,*.exit]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(IPV4, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [IP],
      helpText: {
        EQ: (
          <>
            Search for files by IPv4 addresses found in them.
            <br />
            Provide an exact address or use wildcards.
            <br />
            Example: <code>{IPV4}192.*</code>
          </>
        ),
        IN: (
          <>
            Search for files with any of the IPv4 addresses in the list.
            <br />
            Provide exact addresses or use wildcards.
            <br />
            Example: <code>{IPV4}[1.0.0.0,1.0.2.1]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(IPV6, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by IPv6 addresses found in them.
            <br />
            Provide an exact address or use wildcards.
            <br />
            If the address contains colons or brackets, quote it.
            <br />
            Example: <code>{IPV6}&quot;*::/16&quot;</code>
          </>
        ),
        IN: (
          <>
            Search for files with any of the IPv6 addresses in the list.
            <br />
            Provide exact addresses or use wildcards.
            <br />
            If addresses contain colons or brackets, quote them.
            <br />
            Example:{" "}
            <code>{IPV6}[&quot;2001:db8*&quot;, &quot;3731:54:&quot;]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(EMAIL, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [EMAIL_STATIC],
      helpText: {
        EQ: (
          <>
            Search for files by email addresses.
            <br />
            Provide an exact email or use wildcards.
            <br />
            Example: <code>{EMAIL}*@Compartir.es</code>
          </>
        ),
        IN: (
          <>
            Search for files containing any of the email addresses in the list.
            <br />
            Provide exact emails or use wildcards.
            <br />
            Example: <code>{EMAIL}[*@gmail.com, *@hotmail.com]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CERT_THUMBPRINT, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by their unique certificate thumbprint.
            <br />
            Example: <br />
            <code>
              {CERT_THUMBPRINT}
              277D42066A68326BA10B1874D393327404287C14A9C9DB1C09D50698952A17DD
            </code>
          </>
        ),
        IN: (
          <>
            Search for files by any of certificate thumbprints in the list.
            <br />
            Example:
            <br />
            <code>
              {CERT_THUMBPRINT}
              [C5E129D68BEF22CAF45A173328F33C244A3BDF3E959D8EC1F15DE556396247FA,
              0DD849CC59EC8005CDB8A0D8CD70CC0F9E8E2F30BC064641997DC93E048F4682]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CERT_ISSUER_ORG, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by certificate issuer organization.
            <br />
            Provide an exact name or use wildcards.
            <br />
            Example: <code>{CERT_ISSUER_ORG}*authority</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of issuer organization names in the list.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>{CERT_ISSUER_ORG}[verisign, microsoft]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CERT_ISSUER_UNIT, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by certificate issuer unit.
            <br />
            Provide an exact name or use wildcards.
            <br />
            Example: <code>{CERT_ISSUER_UNIT}*root*</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of issuer unit names in the list.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example:{" "}
            <code>{CERT_ISSUER_UNIT}[&quot;trust network&quot;, *root*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CERT_ISSUER_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by certificate issuer authority (CA).
            <br />
            Provide an exact name or use wildcards.
            <br />
            Example: <code>{CERT_ISSUER_NAME}COMODO</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of issuer authority names in the list.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>{CERT_ISSUER_NAME}[microsoft, *VeriSign*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CERT_ISSUER_COUNTRY, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find files by the country code in the country name property field
            <br />
            of the issuer of the certificate used to sign the file.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>cert-issuer-country: US</code>
          </>
        ),
        IN: (
          <>
            Find files with any of the listed country codes in the country name
            <br />
            property field of the issuer of the certificate used to sign the
            file.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>cert-issuer-country:[Z?,G*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CERT_SUBJECT_ORG, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by certificate authority organization.
            <br />
            Provide an exact name or use wildcards.
            <br />
            Example: <code>{CERT_SUBJECT_ORG}apple</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of authority organization names in the list.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>{CERT_SUBJECT_ORG}[apple, Microsoft]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CERT_SUBJECT_UNIT, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by unit to which certificate was issued.
            <br />
            Provide an exact name or use wildcards.
            <br />
            Example:{" "}
            <code>{CERT_SUBJECT_UNIT}&quot;Developer Relations&quot;</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of unit names in the list.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example:{" "}
            <code>
              {CERT_SUBJECT_UNIT}[Developer*, &quot;Trust Network&quot;]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CERT_SUBJECT_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by organization to which certificate was issued.
            <br />
            Provide an exact name or use wildcards.
            <br />
            Example: <code>{CERT_SUBJECT_NAME}Piriform</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of certificate recipient organization names
            in the list.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>{CERT_SUBJECT_NAME}[cinectic*, google]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CERT_SUBJECT_COUNTRY, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find files by the country code in the country name property field
            <br />
            of the subject to which the certificate has been issued
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>cert-subject-country:DE</code>
          </>
        ),
        IN: (
          <>
            Find files with any of the listed country codes in the country name
            <br />
            property field of the subject to which the certificate has been
            issued.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>cert-subject-country:[US, B*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CERT_SERIAL, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by certificate serial number.
            <br />
            Provide an exact number.
            <br />
            Example: <br />
            <code>{CERT_SERIAL}6101CF3E00000000000F</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of certificate serial numbers in the list.
            <br />
            Provide exact numbers.
            <br />
            Example: <br />
            <code>
              {CERT_SERIAL}[0EBFEA68D677B3E26CAB41C33F3E69DE,
              4B48B27C8224FE37B17A6A2ED7A81C9F]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CERTIFICATE, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by certificate issuer and certificate
            <br />
            subject name, unit, and organization.
            <br />
            Example (case-insensitive wildcard matching is supported):
            <br />
            <code>{CERTIFICATE}*micr*</code>
          </>
        ),
        IN: (
          <>
            Search for files by certificate issuer and certificate subject name,
            <br />
            unit, and organization using each value in the list.
            <br />
            Example (case-insensitive wildcard matching is supported):
            <br />
            <code>{CERTIFICATE}[microsoft, *VeriSign*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(CERTIFICATE_COUNTRY, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by certificate issuer and certificate subject
            <br />
            country.
            <br />
            Example (case-insensitive wildcard matching is supported):
            <br />
            <code>{CERTIFICATE_COUNTRY}US</code>
          </>
        ),
        IN: (
          <>
            Search for files by certificate issuer and certificate subject
            <br />
            country using each value in the list.
            <br />
            Example (case-insensitive wildcard matching is supported):
            <br />
            <code>{CERTIFICATE_COUNTRY}[US, DE]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SECTION, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by PE, ELF, and Mach-O section name.
            <br />
            Example (case-insensitive wildcard matching is supported):
            <br />
            <code>{SECTION}*data</code>
          </>
        ),
        IN: (
          <>
            Search for files by PE, ELF, and Mach-O section name using each
            <br />
            value in the list.
            <br />
            Example (case-insensitive wildcard matching is supported):
            <br />
            <code>{SECTION}[.ndata, bss]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SEGMENT, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by ELF and Mach-O segment.
            <br />
            Example (case-insensitive wildcard matching is supported):
            <br />
            <code>{SEGMENT}page*</code>
          </>
        ),
        IN: (
          <>
            Search for files by ELF and Mach-O segment using each value in the
            <br />
            list.
            <br />
            Example (case-insensitive wildcard matching is supported):
            <br />
            <code>{SEGMENT}[pagezero, text]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SOFTWARE, {
      operators: [EQ, IN],
      helpText: {
        EQ: (
          <>
            Search for files by software package, description, and author.
            <br />
            Example (The keyword does not accept wildcards):
            <br />
            <code>{SOFTWARE}&quot;James Newton-King&quot;</code>
          </>
        ),
        IN: (
          <>
            Search for files by software package, description, and author using
            <br />
            each value in the list.
            <br />
            Example (The keyword does not accept wildcards):
            <br />
            <code>
              {SOFTWARE}[Microsoft, &quot;This package consists of multiple
              activities that simplify the processes in Excel.&quot;]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOCUMENT, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by document author, subject, title, and
            <br />
            description.
            <br />
            Example (case-insensitive wildcard matching is supported):
            <br />
            <code>{DOCUMENT}*soft</code>
          </>
        ),
        IN: (
          <>
            Search for files by document author, subject, title, and description
            <br />
            using each value in the list.
            <br />
            Example (case-insensitive wildcard matching is supported):
            <br />
            <code>{DOCUMENT}[adobe, microsoft, *confidential]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PDB_PATH, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [PDB],
      helpText: {
        EQ: (
          <>
            Search for files associated with specific PDB paths.
            <br />
            Provide an exact path or use wildcards.
            <br />
            If the path contains colons or brackets, quote it.
            <br />
            Example:{" "}
            <code>{PDB_PATH}&quot;D:\\Dev\\Tin7\\InstallDir\\&quot;</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of PDB paths in the list.
            <br />
            Provide exact paths or use wildcards.
            <br />
            If the paths contain colons or brackets, quote them.
            <br />
            Example:{" "}
            <code>
              {PDB_PATH}[&quot;C:\\Windows&quot;, &quot;c:\\Program
              Files\\Perforce&quot;]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_TIMESTAMP, {
      operators: [EQ],
      aliases: [PETS],
      helpText: {
        EQ: (
          <>
            Search for PE files by their compilation date.
            <br />
            The date can be exact or a range of dates in UTC timestamp format.
            <br />
            Example: <code>
              {PE_TIMESTAMP}[* TO 2018-03-06T10:57:29Z]
            </code>, <code>{PE_TIMESTAMP}2018-03-06T10:57:29Z-</code>,{" "}
            <code>{PE_TIMESTAMP}3w-</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_COMPANY_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by associated company name.
            <br />
            Provide an exact name or use wildcards.
            <br />
            Example: <code>{PE_COMPANY_NAME}*enix</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of company names in the list.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>{PE_COMPANY_NAME}[microsoft, ADOBE]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_PRODUCT_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by associated product (application) name.
            <br />
            Provide an exact name or use wildcards.
            <br />
            Example: <code>{PE_PRODUCT_NAME}*setup*</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of product names in the list.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>{PE_PRODUCT_NAME}[*shop, *alms*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_ORIGINAL_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by their original filename.
            <br />
            Provide an exact name or use wildcards.
            <br />
            Example: <code>{PE_ORIGINAL_NAME}crack*</code>
          </>
        ),
        IN: (
          <>
            Search for files by any exact filename or part of the filename in
            the list.
            <br />
            Example: <code>{PE_ORIGINAL_NAME}[crack*, install]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(MUTEX, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by their mutexes.
            <br />
            Provide an exact mutex.
            <br />
            Example: <code>{MUTEX}111c</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of mutexes in the list.
            <br />
            Provide exact mutexes.
            <br />
            Example: <code>{MUTEX}[111c, 2124]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOCUMENT_AUTHOR, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by associated document authors.
            <br />
            Provide an exact value or use wildcards.
            <br />
            Example: <code>{DOCUMENT_AUTHOR}*team*</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of document authors in the list.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>{DOCUMENT_AUTHOR}[adobe, microsoft]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOCUMENT_SUBJECT, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by the &quot;Subject&quot; file property.
            <br />
            Provide an exact value or use wildcards.
            <br />
            Example: <code>{DOCUMENT_SUBJECT}&quot;generated on&quot;</code>
          </>
        ),
        IN: (
          <>
            Search for files by any &quot;Subject&quot; file property in the
            list.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example:{" "}
            <code>{DOCUMENT_SUBJECT}[*search, &quot;generated on&quot;]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOCUMENT_TITLE, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by the &quot;Title&quot; file property.
            <br />
            Provide an exact value or use wildcards.
            <br />
            Example: <code>{DOCUMENT_TITLE}&quot;*Powered by*&quot;</code>
          </>
        ),
        IN: (
          <>
            Search for files by any &quot;Title&quot; file property in the list.
            <br />
            Provide exact value or use wildcards.
            <br />
            Example: <code>{DOCUMENT_TITLE}[*free*, *readme*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOCUMENT_VERSION, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by the &quot;Document Version&quot; file property.
            <br />
            Provide an exact value or use wildcards.
            <br />
            Example: <code>{DOCUMENT_VERSION}1.7</code>
          </>
        ),
        IN: (
          <>
            Search for files by any &quot;Document Version&quot; file property
            in the list.
            <br />
            Provide exact value or use wildcards.
            <br />
            Example: <code>{DOCUMENT_VERSION}[1.7, 2.*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOCUMENT_DESCRIPTION, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [DOC_DESCRIPTION],
      helpText: {
        EQ: (
          <>
            Find files by the document description field, as provided by the
            document author.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>document-description:*Math*</code>
          </>
        ),
        IN: (
          <>
            Find files with any of the listed document description fields.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example:{" "}
            <code>
              document-description:[&quot;Carta personal&quot;, *confidencial]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOCUMENT_PAGES, {
      operators: [EQ, EQ_WILDCARD, RANGE],
      aliases: [DOC_PAGES],
      helpText: {
        EQ: (
          <>
            Find files by the number of pages they contain.
            <br />
            For spreadsheet documents, it is the number of sheets.
            <br />
            Only exact integer values are supported.
            <br />
            Example: <code>document-pages:73</code>
          </>
        ),
        RANGE: (
          <>
            Find files containing more/less pages than a specific number.
            <br />
            Example: <code>document-pages:[4 TO *]</code>,{" "}
            <code>document-pages:4+</code>
            <br />
            Find files with the number of pages within the range.
            <br />
            Example: <code>document-pages:[4 TO 20]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(URI_STATIC, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by URI strings detected by Spectra Core.
            <br />
            Provide an exact URI (IP, URL, domain) or use wildcards.
            <br />
            Example: <code>{URI_STATIC}mozilla.org*</code>
          </>
        ),
        IN: (
          <>
            Search for files with any of the URI strings in the list.
            <br />
            Provide exact URIs (IPs, URLs, domains) or use wildcards.
            <br />
            Example: <code>{URI_STATIC}[*.tor, *.onion,*.exit]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(URI_DYNAMIC, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by URI strings obtained from dynamic analysis
            (Cuckoo).
            <br />
            Provide an exact URI (IP, URL, domain) or use wildcards.
            <br />
            Example: <code>{URI_DYNAMIC}mozilla.org*</code>
          </>
        ),
        IN: (
          <>
            Search for files with any of the URI strings in the list.
            <br />
            Provide exact URIs (IPs, URLs, domains) or use wildcards.
            <br />
            Example: <code>{URI_DYNAMIC}[*.tor, *.onion,*.exit]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(URI_CONFIG, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [C2],
      helpText: {
        EQ: (
          <>
            Search for files by malware uri configuration.
            <br />
            Provide an exact value or use wildcards.
            <br />
            Example: <code>{URI_CONFIG}*dns*</code>
          </>
        ),
        IN: (
          <>
            Search for files by any exact uri configuration or parts of commands
            and controls in the list.
            <br />
            Example: <code>{URI_CONFIG}[*dns*, *.onion*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(IPV4_STATIC, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by IPv4 addresses detected by Spectra Core.
            <br />
            Provide an exact address or use wildcards.
            <br />
            Example: <code>{IPV4_STATIC}192.*</code>
          </>
        ),
        IN: (
          <>
            Search for files with any of the IPv4 addresses in the list.
            <br />
            Provide exact addresses or use wildcards.
            <br />
            Example: <code>{IPV4_STATIC}[1.0.0.0, 1.0.2.1]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(IPV4_DYNAMIC, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by IPv4 addresses obtained from dynamic analysis
            (Cuckoo).
            <br />
            Provide an exact address or use wildcards.
            <br />
            Example: <code>{IPV4_DYNAMIC}192.*</code>
          </>
        ),
        IN: (
          <>
            Search for files with any of the IPv4 addresses in the list.
            <br />
            Provide exact addresses or use wildcards.
            <br />
            Example: <code>{IPV4_DYNAMIC}[1.0.0.0, 1.0.2.1]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(IPV6_STATIC, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by IPv6 addresses detected by Spectra Core.
            <br />
            Provide an exact address or use wildcards.
            <br />
            If the address contains colons or brackets, quote it.
            <br />
            Example: <code>{IPV6_STATIC}&quot;*::/16&quot;</code>
          </>
        ),
        IN: (
          <>
            Search for files with any of the IPv6 addresses in the list.
            <br />
            Provide exact addresses or use wildcards.
            <br />
            If addresses contain colons or brackets, quote them.
            <br />
            Example:{" "}
            <code>
              {IPV6_STATIC}[&quot;2001:db8*&quot;, &quot;3731:54:&quot;]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(MUTEX_CONFIG, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by their malware configuration mutexes detected by
            Spectra Core.
            <br />
            Provide an exact mutex.
            <br />
            Example: <code>{MUTEX_CONFIG}111c</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of mutexes in the list.
            <br />
            Provide exact mutexes.
            <br />
            Example: <code>{MUTEX_CONFIG}[111c, 2124]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(MUTEX_DYNAMIC, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files by their malware configuration mutexes obtained
            from dynamic analysis.
            <br />
            Provide an exact mutex.
            <br />
            Example: <code>{MUTEX_DYNAMIC}111c</code>
          </>
        ),
        IN: (
          <>
            Search for files by any of mutexes in the list.
            <br />
            Provide exact mutexes.
            <br />
            Example: <code>{MUTEX_DYNAMIC}[111c, 2124]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_LANGUAGE, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find PE files with at least one resource in the specified language,
            or Document files with matching EXIF language property. Keyword is
            case-insensitive and wildcards are supported. <br />
            Example: <code>{PE_LANGUAGE}russian</code>
          </>
        ),
        IN: (
          <>
            Find PE or Document files with resources matching any of the
            languages in the list. Keyword is case-insensitive and wildcards are
            supported. <br />
            Example: <code>{PE_LANGUAGE}[Eng*, russian]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_IMPORT, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [IMPORTS],
      helpText: {
        EQ: (
          <>
            Search for PE files by imported library name.
            <br />
            Provide an exact library name or use wildcards.
            <br />
            Example: <code>pe-import:URLMON.DLL</code>
          </>
        ),
        IN: (
          <>
            Search for PE files by any imported library name in the list.
            <br />
            Provide exact library names or use wildcards.
            <br />
            Example: <code>pe-import:[win*, url*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_FUNCTION, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for PE files by API-related metadata.
            <br />
            Provide an exact string or use wildcards.
            <br />
            Example: <code>pe-function:RegEnum*</code>
          </>
        ),
        IN: (
          <>
            Search for PE files with any of the API-related metadata in the
            list.
            <br />
            Provide exact strings or use wildcards.
            <br />
            Example: <code>pe-function:[RegEnumKeyW, GetUserNameA]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_EXPORT, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [EXPORTS],
      helpText: {
        EQ: (
          <>
            Search for PE files by exported symbol name.
            <br />
            Provide the exact name or use wildcards.
            <br />
            Example: <code>pe-export:MS*</code>
          </>
        ),
        IN: (
          <>
            Search for PE files with any of the exported symbol names in the
            list.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>pe-export:[GetMemoSize, DeleteFile]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_RESOURCE, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for PE files by resource type or name.
            <br />
            Provide an exact string or use wildcards.
            <br />
            Example: <code>pe-resource:Properties</code>
          </>
        ),
        IN: (
          <>
            Search for PE files with any of the resources in the list.
            <br />
            Provide exact resource type or name, or use wildcards.
            <br />
            Example: <code>pe-resource:[Tcpview, Aboutbox]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_SECTION_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for PE files by their section names.
            <br />
            Provide an exact name or use wildcards.
            <br />
            Example: <code>pe-section-name:*rdata</code>
          </>
        ),
        IN: (
          <>
            Search for PE files with any of the sections in the list.
            <br />
            Provide exact section names or use wildcards.
            <br />
            Example: <code>pe-section-name:[.Rdata, .Ndata, *rsrc]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_SECTION_COUNT, {
      operators: [EQ, RANGE],
      helpText: {
        EQ: (
          <>
            Search for PE files by the exact number of sections they contain
            <br />
            Example: <code>pe-section-count:15</code>
          </>
        ),
        RANGE: (
          <>
            Search for PE files with the number of sections within the range.
            For greater/less than values, use wildcards. Examples:{" "}
            <code>pe-section-count:[5 TO *]</code>,
            <code>pe-section-count:5+</code>,
            <code>pe-section-count:[2 TO 10]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_DESCRIPTION, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find PE files by the contents of their file description field
            <br />
            in version information metadata.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>pe-description:*proged</code>
          </>
        ),
        IN: (
          <>
            Find PE files by any of the listed contents of their file
            description
            <br />
            field in version information metadata.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example:{" "}
            <code>pe-description:[DisplaySwitch, WizardFramework]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_COPYRIGHT, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find PE files by the contents of their legal copyright field
            <br />
            in version information metadata.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>pe-copyright:Copyright*</code>
          </>
        ),
        IN: (
          <>
            Find PE files by any of the listed contents of their legal copyright
            <br />
            field in version information metadata.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>pe-copyright:[*Corporation, regsvr32]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_SECTION_SHA1, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [PE_SECTION_HASH],
      helpText: {
        EQ: (
          <>
            Find PE files by the SHA1 hash calculated for their section part.
            <br />
            Use hash values calculated by ReversingLabs products with this
            keyword.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            Example:{" "}
            <code>
              pe-section-sha1:7640a007e39b487bf1dbbde6487724faa131f6a8
            </code>
          </>
        ),
        IN: (
          <>
            Find PE files by any of the listed SHA1 hashes calculated for their
            <br />
            section part. Use hash values calculated by ReversingLabs products.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            Example: <code>pe-section-sha1:[hash1, hash2, hash3]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_OVERLAY_SHA1, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [PE_OVERLAY_HASH],
      helpText: {
        EQ: (
          <>
            Find PE files by the SHA1 hash calculated for their overlay part.
            <br />
            Use hash values calculated by ReversingLabs products with this
            keyword.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            Example:{" "}
            <code>
              pe-overlay-sha1:4b4a2436b827d42b204b1f112b45d7a6d1b7ca52
            </code>
          </>
        ),
        IN: (
          <>
            Find PE files by any of the listed SHA1 hashes calculated for their
            <br />
            overlay part. Use hash values calculated by ReversingLabs products.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            Example: <code>pe-overlay-sha1:[hash1, hash2, hash3]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PE_RESOURCE_SHA1, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [PE_RESOURCE_HASH],
      helpText: {
        EQ: (
          <>
            Find PE files by the SHA1 hash calculated for their resources part.
            <br />
            Use hash values calculated by ReversingLabs products with this
            keyword.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            Example:{" "}
            <code>
              pe-resource-sha1:4260284ce14278c397aaf6f389c1609b0ab0ce51
            </code>
          </>
        ),
        IN: (
          <>
            Find PE files by any of the listed SHA1 hashes calculated for their
            <br />
            resources part. Use hash values calculated by ReversingLabs
            products.
            <br />
            This keyword is case-sensitive and doesn&apos;t accept wildcards.
            <br />
            Example: <code>pe-resource-sha1:[hash1, hash2, hash3]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(TAGGANT_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for PE files by name of the packer that was used to pack
            them.
            <br />
            Provide an exact packer name or use wildcards.
            <br />
            Example: <code>taggant-name:themida</code>
          </>
        ),
        IN: (
          <>
            Search for PE files that were packed by any of the packers in the
            list.
            <br />
            Provide exact packer names or use wildcards.
            <br />
            Example: <code>taggant-name:[enigma*, vmpack*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SUBMISSIONS, {
      operators: [EQ, RANGE],
      helpText: {
        EQ: (
          <>
            Search for files by the amount of times they&apos;ve been submitted
            for analysis.
            <br />
            Example: <code>submissions:3</code>
          </>
        ),
        RANGE: (
          <>
            Search for greater/less than values or the exact range of values.
            <br />
            Examples: <code>submissions:[3 TO *]</code>,{" "}
            <code>submissions:3-</code>,<code>submissions:[2 TO 4]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(LAST_ANALYSIS, {
      operators: [EQ, RANGE],
      aliases: [LA],
      helpText: {
        EQ: (
          <>
            Search for files by the date of their last AV scan.
            <br />
            Example: <code>{LAST_ANALYSIS}2018-05-17T11:27:19Z</code>
          </>
        ),
        RANGE: (
          <>
            Search for files that were last scanned by AV engines in the
            specified range.
            <br />
            Example:{" "}
            <code>
              {LAST_ANALYSIS}[2018-05-17T11:27:19Z TO 2018-05-24T11:27:19Z]
            </code>
            ,<code>{LAST_ANALYSIS}2018-05-24T11:27:19Z-</code>,
            <code>{LAST_ANALYSIS}2m+</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(URI_SOURCE, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [ITW],
      helpText: {
        EQ: (
          <>
            Search for files by URI source from which they were downloaded.
            <br />
            Provide an exact URI or use wildcards.
            <br />
            Example: <code>uri-source:*warez*</code>
          </>
        ),
        IN: (
          <>
            Search for files retrieved from any of the URI sources in the list.
            <br />
            Provide exact URIs or use wildcards.
            <br />
            Example: <code>uri-source:[softonic.com, *cnet.com]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(ANDROID_APP_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for Android apps by process name.
            <br />
            Provide the exact name or use wildcards.
            <br />
            Example: <code>android-app-name:*SkypeApplication*</code>
          </>
        ),
        IN: (
          <>
            Search for Android apps by any of the listed process names.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>android-app-name:[MainApp, *alt.ywuajgf*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(ANDROID_PACKAGE, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for Android apps by package name.
            <br />
            Provide the exact name or use wildcards.
            <br />
            Example: <code>android-package:*com.picklieapps.player*</code>
          </>
        ),
        IN: (
          <>
            Search for Android apps by any of the listed package names.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>android-package:[*ruckygames*, *skype.raider*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(ANDROID_PERMISSION, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for Android apps by their permissions.
            <br />
            Provide the exact string or use wildcards.
            <br />
            Example: <code>android-permission:*WRITE_SETTINGS*</code>
          </>
        ),
        IN: (
          <>
            Search for Android apps by any of the listed permissions.
            <br />
            Provide exact strings or use wildcards.
            <br />
            Example:{" "}
            <code>android-permission:[*storage*, *disable_keyguard*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(ANDROID_FEATURES, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for Android apps by their features.
            <br />
            Provide the exact string or use wildcards.
            <br />
            Example: <code>android-features:*hardware.camera*</code>
          </>
        ),
        IN: (
          <>
            Search for Android apps by any of the listed features.
            <br />
            Provide exact strings or use wildcards.
            <br />
            Example: <code>android-features:[camera, telephony]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(ANDROID_IMPORT, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find Android applications by a shared library they are linked
            against.
            <br />
            Provide the exact library name or use wildcards.
            <br />
            Example: <code>android-import:org.apache.http.legacy*</code>
          </>
        ),
        IN: (
          <>
            Find Android applications by one or more shared libraries
            <br />
            the applications are linked against.
            <br />
            Provide exact library names or use wildcards.
            <br />
            Example: <code>android-import:[sec_fe?ture, *google*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(IOS_APP_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for iOS apps by name.
            <br />
            Provide the exact name or use wildcards.
            <br />
          </>
        ),
        IN: (
          <>
            Search for iOS apps by any of the listed names.
            <br />
            Provide exact names or use wildcards.
            <br />
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(IOS_PACKAGE, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for iOS apps by package name.
            <br />
            Provide the exact name or use wildcards.
            <br />
          </>
        ),
        IN: (
          <>
            Search for iOS apps by any of the listed package names.
            <br />
            Provide exact names or use wildcards.
            <br />
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(IOS_AUTHOR, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for iOS apps by author name.
            <br />
            Provide the exact name or use wildcards.
            <br />
          </>
        ),
        IN: (
          <>
            Search for iOS apps by any of the listed author names.
            <br />
            Provide exact names or use wildcards.
            <br />
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(BROWSER_PACKAGE, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for browser extensions by package name.
            <br />
            Supported package formats: Chrome, Safari, Firefox.
            <br />
            Provide the exact name or use wildcards.
            <br />
            Example: <code>browser-package:*Click2Save*</code>
          </>
        ),
        IN: (
          <>
            Search for browser extensions by any of the listed package names.
            <br />
            Supported package formats: Chrome, Safari, Firefox.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>browser-package:[*priiceechOp*, *iCalc*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(THIRD_PARTY_LIBRARY, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for PE files by third-party library name.
            <br />
            Provide the exact name or use wildcards.
            <br />
          </>
        ),
        IN: (
          <>
            Search for PE files by any of the listed third-party library names.
            <br />
            Provide exact names or use wildcards.
            <br />
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(THIRD_PARTY_PUBLISHER, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for PE files by third-party library publisher.
            <br />
            Provide the exact string or use wildcards.
            <br />
          </>
        ),
        IN: (
          <>
            Search for PE files by any of the listed third-party publishers.
            <br />
            Provide exact strings or use wildcards.
            <br />
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOTNET_MODULE_ID, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for .NET files by module ID.
            <br />
            Provide the exact string or use wildcards.
            <br />
            Example: <code>dotnet-module-id:*20DEC3DA-523F*</code>
          </>
        ),
        IN: (
          <>
            Search for .NET files by any of the listed module IDs.
            <br />
            Provide exact strings or use wildcards.
            <br />
            Example:{" "}
            <code>dotnet-module-id:[*9249F5D0-1821*, *E133ACC7-60C9*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOTNET_MODULE_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for .NET files by module name.
            <br />
            Provide the exact name or use wildcards.
            <br />
            Example: <code>dotnet-module-name:TeSt.exe</code>
          </>
        ),
        IN: (
          <>
            Search for .NET files by any of the listed module names.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>dotnet-module-name:[Posh.exe, adobe.exe]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOTNET_RESOURCE, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for .NET files by resource name.
            <br />
            Provide the exact name or use wildcards.
            <br />
            Example: <code>dotnet-resource:&quot;Hidden Tear&quot;</code>
          </>
        ),
        IN: (
          <>
            Search for .NET files by any of the listed resource names.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>dotnet-resource:[*Orcus*, *Clientloaderform*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOTNET_ASSEMBLY, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for .NET files by assemblies they reference.
            <br />
            Provide the exact assembly name or use wildcards.
            <br />
            Example: <code>dotnet-assembly:*mscorlib*</code>
          </>
        ),
        IN: (
          <>
            Search for .NET files by any of the listed assemblies.
            <br />
            Provide exact assembly names or use wildcards.
            <br />
            Example:{" "}
            <code>
              dotnet-assembly:[*iJnJWYUQA*, &quot;NanoCore Client&quot;]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOTNET_TYPE_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for .NET files by type names.
            <br />
            Provide the exact name or use wildcards.
            <br />
          </>
        ),
        IN: (
          <>
            Search for .NET files by any of the listed type names.
            <br />
            Provide exact names or use wildcards.
            <br />
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOTNET_METHOD_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find .NET files by method names their classes call to perform an
            action.
            <br />
            Both public and private methods are searchable.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>dotnet-method-name:get_Url</code>
          </>
        ),
        IN: (
          <>
            Find .NET files by any of the listed method names
            <br />
            their classes call to perform an action.
            <br />
            Both public and private methods are searchable.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>dotnet-method-name:[?oadCompl*, *HoldEnd]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DEX_CLASS_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for DEX files by class names.
            <br />
            Provide the exact name or use wildcards.
            <br />
          </>
        ),
        IN: (
          <>
            Search for DEX files by any of the listed class names.
            <br />
            Provide exact names or use wildcards.
            <br />
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DEX_METHOD_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find DEX files by method names their classes call to perform an
            action.
            <br />
            Both public and private methods are searchable.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>dex-method-name:unregisterCallB*</code>
          </>
        ),
        IN: (
          <>
            Find DEX files by any of the listed method names
            <br />
            their classes call to perform an action.
            <br />
            Both public and private methods are searchable.
            <br />
            Provide exact values or use wildcards.
            <br />
            Example: <code>dex-method-name:[getLocation, invok*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(EXIF, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for multimedia files by their EXIF properties.
            <br />
            Provide the exact string or use wildcards.
            <br />
          </>
        ),
        IN: (
          <>
            Search for multimedia files by any of the listed EXIF properties.
            <br />
            Provide exact strings or use wildcards.
            <br />
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(TAG_YARA, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for samples classified by YARA using a YARA rule tag.
            <br />
            Provide the exact tag name or use wildcards.
            <br />
            Example: <code>tag-yara:malicious</code>
          </>
        ),
        IN: (
          <>
            Search for samples by any of the listed YARA rule tags.
            <br />
            Provide exact tag names or use wildcards.
            <br />
            Example: <code>tag-yara:[malicious, suspicious]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(TAG_USER, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for samples tagged with custom, user-assigned tags.
            <br />
            Provide the exact tag name or use wildcards.
            <br />
            Example: <code>tag-user:custom-t*</code>
          </>
        ),
        IN: (
          <>
            Search for samples tagged with any of the listed custom tags.
            <br />
            Provide exact tag names or use wildcards.
            <br />
            Example: <code>tag-user:[custom1, CUSTOM2, custom?]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOMAIN, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files associated with a specific domain.
            <br />
            Provide exact domain names or use wildcards.
            <br />
            Example: <code>domain:salesforce.com</code>
          </>
        ),
        IN: (
          <>
            Search for files associated with any of the listed domains.
            <br />
            Example: <code>domain:[gutenberg.org, microcenter.com]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(ACTOR, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files based on the associated APT actor name.
            <br />
            Provide the exact actor name or use wildcards.
            <br />
            Example: <code>actor:GoblinPanda</code>
          </>
        ),
        IN: (
          <>
            Search for files based on any of the listed APT actor names.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>actor:[Hellsing, Cycldek]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(VERTICAL, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files based on the vertical feed type in which they were
            found.
            <br />
            Case-insensitive wildcard matching is supported.
            <br />
            Example: <code>vertical:ransomware</code>
          </>
        ),
        IN: (
          <>
            Search for files with any of the listed vertical feed types.
            <br />
            Case-insensitive wildcard matching is supported.
            <br />
            Example: <code>vertical:[ransomware,apt,financial]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(ELF_SECTION_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for ELF files by their section names.
            <br />
            Provide an exact name or use wildcards.
            <br />
            Example: <code>elf-section-name:*data</code>
          </>
        ),
        IN: (
          <>
            Search for ELF files by any of the listed section names.
            <br />
            Provide exact section names or use wildcards.
            <br />
            Example: <code>elf-section-name:[.rodata, .ndata, .bss]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(ELF_SECTION_COUNT, {
      operators: [EQ, RANGE],
      helpText: {
        EQ: (
          <>
            Search for ELF files by the exact number of sections they contain.
            <br />
            Example: <code>elf-section-count:10</code>
          </>
        ),
        RANGE: (
          <>
            Search for ELF files with the number of sections within the range.
            <br />
            For greater/less than values, use wildcards.
            <br />
            Examples: <code>elf-section-count:[5 TO *]</code>,
            <code>elf-section-count:5+</code>,<code>elf-section-count:5-</code>,
            <code>elf-section-count:[5 TO 15]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(ELF_SYMBOL, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for ELF files by their symbol names.
            <br />
            Provide the exact name or use wildcards.
            <br />
            Example: <code>elf-symbol:mar*</code>
          </>
        ),
        IN: (
          <>
            Search for ELF files by any of the listed symbol names.
            <br />
            Provide exact names or use wildcards.
            <br />
            Example: <code>elf-symbol:[fopen, fprintf, mkdir]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(MACHO_SECTION_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for MachO files by their section names.
            <br />
            Provide an exact name or use wildcards.
            <br />
            Example: <code>macho-section-name:data</code>
          </>
        ),
        IN: (
          <>
            Search for MachO files by any of the listed section names.
            <br />
            Provide exact section names or use wildcards.
            <br />
            Example: <code>macho-section-name:[bss, common, data]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(MACHO_SECTION_COUNT, {
      operators: [EQ, RANGE],
      helpText: {
        EQ: (
          <>
            Search for MachO files by the exact number of sections they contain.
            <br />
            Example: <code>macho-section-count:10</code>
          </>
        ),
        RANGE: (
          <>
            Search for MachO files with the number of sections within the range.
            <br />
            For greater/less than values, use wildcards.
            <br />
            Examples: <code>macho-section-count:[5 TO *]</code>,{" "}
            <code>macho-section-count:5+</code>,
            <code>macho-section-count:[5 TO 15]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(MACHO_SYMBOL, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for MachO files by their symbol names.
            <br />
            Provide an exact symbol name or use wildcards.
            <br />
            Example: <code>macho-symbol:f*</code>
          </>
        ),
        IN: (
          <>
            Search for MachO files by any of the listed symbol names.
            <br />
            Provide exact symbol names or use wildcards.
            <br />
            Example: <code>macho-symbol:[exit, malloc, umask]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(MACHO_SEGMENT_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [MACHO_SEGMENT],
      helpText: {
        EQ: (
          <>
            Search for MachO files by their segment names.
            <br />
            Provide an exact segment name or use wildcards.
            <br />
            Example: <code>macho-segment:pagezero</code>
          </>
        ),
        IN: (
          <>
            Search for MachO files by any of the listed segment names.
            <br />
            Provide exact segment names or use wildcards.
            <br />
            Example: <code>macho-segment:[linkedit, pagezero, text]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(MACHO_SEGMENT_COUNT, {
      operators: [EQ, RANGE],
      helpText: {
        EQ: (
          <>
            Find MachO files by the count of segments they contain.
            <br />
            Only exact integer values are supported.
            <br />
            Example: <code>macho-segment-count:30</code>
          </>
        ),
        RANGE: (
          <>
            Find MachO files with more/less segments than a specific number.
            <br />
            Example: <code>macho-segment-count:[11 TO *]</code>
            <br />
            <code>macho-segment-count:[11 TO *]</code>
            <br />
            <code>macho-segment-count:11+</code>
            <br />
            Find Mach0 files with the segment count within the range.
            <br />
            Example: <code>macho-segment-count:[2 TO 8]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(MACHO_IMPORT, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for MachO files by imported library name.
            <br />
            Provide an exact library name or use wildcards.
            <br />
            Example: <code>macho-import:\/usr\/lib\/\*</code>
          </>
        ),
        IN: (
          <>
            Search for MachO files by any imported library name in the list.
            <br />
            Provide exact library names or use wildcards.
            <br />
            Example:{" "}
            <code>
              macho-import:[/usr/lib/libgcc_s.1.dylib,
              /usr/lib/libSystem.B.dylib]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(EMAIL_FROM, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find emails or files associated with emails by their sender.
            <br />
            Provide the exact sender address or use wildcards.
            <br />
            Example:<code>email-from:aguilarb@melag.de</code>
          </>
        ),
        IN: (
          <>
            Find emails or files associated with emails by any of the listed
            senders.
            <br />
            Provide exact addresses or use wildcards.
            <br />
            Example:<code>email-from:[*@yahoo.com, *noreply*, admin@*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(EMAIL_TO, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find emails or files associated with emails by their recipient.
            <br />
            Provide the exact recipient address or use wildcards.
            <br />
            Example:<code>email-to:intruder@logickorea.co.kr</code>
          </>
        ),
        IN: (
          <>
            Find emails or files associated with emails by any of the listed
            recipients.
            <br />
            Provide exact addresses or use wildcards.
            <br />
            Example:<code>email-to:[*@electroputere.ro, *@143b389c953.pe]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(EMAIL_SUBJECT, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find emails or files associated with emails by their subject.
            <br />
            Provide the exact email subject or use wildcards.
            <br />
            Example:<code>email-subject:&quot;New Order&quot;</code>
          </>
        ),
        IN: (
          <>
            Find emails or files associated with emails by any of the listed
            subjects.
            <br />
            Provide exact email subjects or use wildcards.
            <br />
            Example:<code>email-subject:[*transaction*, *payment*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(EMAIL_X_KEY, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find emails or files associated with emails by custom header names.
            <br />
            Provide the exact custom header name or use wildcards.
            <br />
            Example:<code>email-x-key:X-Mailer</code>
          </>
        ),
        IN: (
          <>
            Find emails or files associated with emails by custom header names.
            <br />
            Provide exact custom header names or use wildcards.
            <br />
            Example:
            <code>email-x-key:[*SpamScore*, *Exchange*, X-Original-To]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(EMAIL_X_VALUE, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Find emails or files associated with emails by custom header values.
            <br />
            Provide the exact value of a custom header or use wildcards.
            <br />
            Example:<code>email-x-value:Anonymous</code>
          </>
        ),
        IN: (
          <>
            Find emails or files associated with emails by custom header values.
            <br />
            Provide exact custom header values or use wildcards.
            <br />
            Example:<code>email-x-value:[*BANNED*, YahooMailWebService*]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SIMILAR_TO, {
      operators: [EQ],
      helpText: {
        EQ: (
          <>
            Search for files that are functionally similar to the requested file
            hash.
            <br />
            Functionally similar files are defined by RHA (ReversingLabs Hashing
            Algorithm) that identifies code similarity between unknown samples
            and previously seen malware samples.
            <br />
            All hash types (MD5, SHA1, SHA256) can be used with this keyword.
            <br />
            Only one similar-to keyword can be used in a single query.
            <br />
            The keyword is case-sensitive and doesn’t support wildcards.
            <br />
            Example:
            <code>similar-to:d3efda3a87c558287ba6cc812caa52f795c945d7</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(ATTACK_TACTIC, {
      operators: [EQ, IN],
      helpText: {
        EQ: (
          <>
            Search for files mapped to the provided Mitre ATT&CK framework
            behavior tactic ID.
            <br />
            Example:
            <code>attack-tactic:TA0007</code>
          </>
        ),
        IN: (
          <>
            Search for files mapped to one or more Mitre ATT&CK framework
            behavior tactic IDs.
            <br />
            Example:
            <code>attack-tactic:[TA0006, TA0007]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(ATTACK_TECHNIQUE, {
      operators: [EQ, IN],
      helpText: {
        EQ: (
          <>
            Search for files mapped to the provided Mitre ATT&CK framework
            behavior technique ID.
            <br />
            Example:
            <code>attack-technique:T1083</code>
          </>
        ),
        IN: (
          <>
            Search for files mapped to one or more Mitre ATT&CK framework
            behavior technique IDs.
            <br />
            Example:
            <code>attack-technique:[T1083, T1082, T1489]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SUBMISSION_TIME, {
      operators: [EQ],
      helpText: {
        EQ: (
          <>
            Search for files by date when they were last submitted
            <br />
            Example:{" "}
            <code>
              {SUBMISSION_TIME}[2017-12-01T11:36:59Z TO 2018-03-06T11:36:59Z]
            </code>
            , <code>{SUBMISSION_TIME}2017-12-01T11:36:59Z+</code>,{" "}
            <code>{SUBMISSION_TIME}1y-</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SUBMISSION_USER, {
      operators: [EQ, IN],
      helpText: {
        EQ: (
          <>
            Search for submissions by the user that has submitted them.
            <br />
            Provide an exact name.
            <br />
            Example: <code>{SUBMISSION_USER}crack*</code>
          </>
        ),
        IN: (
          <>
            Search for submissions by the users in the list.
            <br />
            Example: <code>{SUBMISSION_USER}[admin, user]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(PROCESSING_STATUS, {
      values: ["unprocessed", "processing", "processed", "error"],
      operators: [EQ, IN],
      helpText: {
        EQ: (
          <>
            Possible values are:
            <code>unprocessed</code>,<code>processing</code>,
            <code>processed</code>,<code>error</code>
            <br />
            Example: <code>{PROCESSING_STATUS}processed</code>
          </>
        ),
        IN: (
          <>
            Possible values are:
            <code>unprocessed</code>,<code>processing</code>,
            <code>processed</code>,<code>error</code>
            <br />
            Example: <code>{PROCESSING_STATUS}[processing, error]</code>
          </>
        )
      }
    }).getSuggestions(),
    ...Object.values(AV_VENDORS_ALIAS).map(vendorName => {
      const prefix = `av-${vendorName}`;
      return new SearchSuggestion(prefix, {
        operators: [EQ, EQ_WILDCARD, IN],
        aliases: [`${vendorName}`],
        helpText: {
          EQ: (
            <>
              Search for all detections or specific malware found by AV vendors.
              <br />
              Examples: <code>{prefix}*</code>, <code>{prefix}Emotet*</code>
            </>
          ),
          IN: (
            <>
              Search for any malware detection in the list for selected AV
              vendor.
              <br />
              Examples: <code>{prefix}[osx, mac, ios]</code>
            </>
          )
        }
      }).getSuggestions();
    }),
    new SearchSuggestion(ELF_SEGMENT_SHA1, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [ELF_SEGMENT_HASH],
      helpText: {
        EQ: (
          <>
            Search for files by the SHA1 hash of their ELF segment. The keyword
            is case-sensitive and doesn’t accept wildcards.
            <br />
            Examples:
            <code>
              {ELF_SEGMENT_SHA1}116e279b55b58e5b9619aac80a8e85bfa9c839fc
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(MACHO_SEGMENT_SHA1, {
      operators: [EQ],
      aliases: [MACHO_SEGMENT_HASH],
      helpText: {
        EQ: (
          <>
            Search for files by the SHA1 hash of their MachO segment. The
            keyword is case-sensitive and doesn’t accept wildcards.
            <br />
            Examples:
            <code>
              {MACHO_SEGMENT_SHA1}116e279b55b58e5b9619aac80a8e85bfa9c839fc
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOTNET_PINVOKE_IMPORT, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for .NET files by pinvoke imports. Case-insensitive wildcard
            matching is supported.
            <br />
            Examples:
            <code>{DOTNET_PINVOKE_IMPORT}kernel32.dll</code>
          </>
        ),
        IN: (
          <>
            Search for .NET files by one or more pinvoke imports.
            Case-insensitive wildcard matching is supported.
            <br />
            Examples:
            <code>{DOTNET_PINVOKE_IMPORT}[“kernel32.dll”, “user32.dll”]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(DOTNET_PINVOKE_FUNCTION, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for .NET files by pinvoke functions. Case-insensitive
            wildcard matching is supported.
            <br />
            Examples:<code>{DOTNET_PINVOKE_FUNCTION}EncodePointer*</code>
          </>
        ),
        IN: (
          <>
            Search for .NET files by one or more pinvoke functions.
            Case-insensitive wildcard matching is supported.
            <br />
            Examples:
            <code>
              {DOTNET_PINVOKE_FUNCTION}[“EncodePointer”, “DecodePointer”]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SIGNER_VALID_FROM, {
      operators: [EQ, RANGE],
      aliases: [CERT_VALID_FROM],
      helpText: {
        EQ: (
          <>
            Search for files that have been signed by certificates valid from a
            specific time.
            <br />
            Example:<code>
              {SIGNER_VALID_FROM}[2018-03-06T10:57:29Z TO *]
            </code>, <code>{SIGNER_VALID_FROM}2018-03-06T10:57:29Z-</code>,{" "}
            <code>{SIGNER_VALID_FROM}3m+</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SIGNER_VALID_TO, {
      operators: [EQ, RANGE],
      aliases: [CERT_VALID_TO],
      helpText: {
        EQ: (
          <>
            Search for files that have been signed by certificates valid to a
            specific time.
            <br />
            Example:<code>
              {SIGNER_VALID_TO}[2018-03-06T10:57:29Z TO *]
            </code>, <code>{SIGNER_VALID_TO}2018-03-06T10:57:29Z-</code>,{" "}
            <code>{SIGNER_VALID_TO}3m+</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(TAGGANT_VALID_FROM, {
      operators: [EQ, RANGE],
      helpText: {
        EQ: (
          <>
            Search for files by the time it was signed using taggant.
            <br />
            Example:<code>{TAGGANT_VALID_FROM}[2018-03-06T10:57:29Z TO *]</code>
            , <code>{TAGGANT_VALID_FROM}2018-03-06T10:57:29Z+</code>,
            <code>{TAGGANT_VALID_FROM}7d+</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(TAGGANT_VALID_TO, {
      operators: [EQ, RANGE],
      helpText: {
        EQ: (
          <>
            Search for files by the expiry time provided by taggant.
            <br />
            Example:<code>
              {TAGGANT_VALID_TO}[2018-03-06T10:57:29Z TO *]
            </code>, <code>{TAGGANT_VALID_TO}2018-03-06T10:57:29Z+</code>,
            <code>{TAGGANT_VALID_TO}7d+</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(APPID_PRODUCT_TYPE, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [APPID_CATEGORY],
      helpText: {
        EQ: (
          <>
            Search for applications and libraries by their type.
            Case-insensitive wildcard matching is supported.
            <br />
            Example:<code>{APPID_PRODUCT_TYPE}browser</code>
          </>
        ),
        IN: (
          <>
            Search for applications and libraries by their type.
            Case-insensitive wildcard matching is supported.
            <br />
            Example:<code>{APPID_PRODUCT_TYPE}[browser, development]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(APPID_PRODUCT_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for files with a matching product name. Case-insensitive
            wildcard matching is supported.
            <br />
            Example:
            <code>{APPID_PRODUCT_NAME}”Mozilla Firefox Plugin Hang UI”</code>
          </>
        ),
        IN: (
          <>
            Search for files with one or more matching product name.
            Case-insensitive wildcard matching is supported.
            <br />
            Example:
            <code>
              {APPID_PRODUCT_NAME}[“Mozilla Firefox Plugin Hang UI”, “Mozilla
              Firefox Helper”]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(APPID_COMPANY_NAME, {
      operators: [EQ, EQ_WILDCARD, IN],
      aliases: [APPID_AUTHOR],
      helpText: {
        EQ: (
          <>
            Search for applications and libraries by their publisher.
            Case-insensitive wildcard matching is supported.
            <br />
            Example:<code>{APPID_COMPANY_NAME}”Mozilla Foundation”</code>
          </>
        ),
        IN: (
          <>
            Search for applications and libraries by their publisher.
            Case-insensitive wildcard matching is supported.
            <br />
            Example:
            <code>
              {APPID_COMPANY_NAME}[“Mozilla Foundation”, “Microsoft
              Corporation”]
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(APPID_DESCRIPTION, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for applications and libraries by their description.
            Case-insensitive wildcard matching is supported.
            <br />
            Example:<code>{APPID_DESCRIPTION}”*Firefox Plugin Hang UI*”</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(INDICATORS, {
      operators: [EQ, IN],
      helpText: {
        EQ: (
          <p>
            Search for files by their static analysis behaviors. The keyword is
            case-sensitive and doesn’t accept wildcards. The full list of
            indicator IDs and their descriptions can be found{" "}
            <a
              href={INDICATOR_DESCRIPTIONS_URL}
              onClick={e => e.stopPropagation()}
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>
            .
            <br />
            Example:<code>{INDICATORS}”2150”</code>
          </p>
        ),
        IN: (
          <p>
            Search for files by one or more of their static analysis behaviors.
            The keyword is case-sensitive and doesn’t accept wildcards. The full
            list of indicator IDs and their descriptions can be found{" "}
            <a
              href={INDICATOR_DESCRIPTIONS_URL}
              onClick={e => e.stopPropagation()}
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>
            .
            <br />
            Example:<code>{INDICATORS}[“2150”, “2102”]</code>
          </p>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SOFTWARE_PACKAGE, {
      operators: [EQ, IN],
      helpText: {
        EQ: (
          <>
            Search for specific software packages. The keyword is case-sensitive
            and doesn’t accept wildcards.
            <br />
            Example:<code>{SOFTWARE_PACKAGE}“tidal“</code>
          </>
        ),
        IN: (
          <>
            Search for specific software packages. The keyword is case-sensitive
            and doesn’t accept wildcards.
            <br />
            Example:<code>{SOFTWARE_PACKAGE}[“tidal“, “AWSSDK.WorkLink”]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SOFTWARE_DESCRIPTION, {
      operators: [EQ],
      helpText: {
        EQ: (
          <>
            Search for software packages by their description.
            <br />
            Example:
            <code>
              {SOFTWARE_DESCRIPTION}”This package consists of multiple
              activities that simplify the processes in Excel.”
            </code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(SOFTWARE_AUTHOR, {
      operators: [EQ, IN],
      helpText: {
        EQ: (
          <>
            Search for software packages by their author/publisher.
            <br />
            Example:<code>{SOFTWARE_AUTHOR}”James Newton-King”</code>
          </>
        ),
        IN: (
          <>
            Search for software packages by their author/publisher.
            <br />
            Example:
            <code>{SOFTWARE_AUTHOR}[“Amazon Web Services”, “Microsoft“]</code>
          </>
        )
      }
    }).getSuggestions(),
    new SearchSuggestion(EXPLOIT, {
      operators: [EQ, EQ_WILDCARD, IN],
      helpText: {
        EQ: (
          <>
            Search for malware that exploits a particular vulnerability.
            Case-insensitive wildcard matching is supported.
            <br />
            Examples:<code>{EXPLOIT}“CVE-2020-0601“</code>,{" "}
            <code>{EXPLOIT}“CVE-2020-*“</code>
          </>
        ),
        IN: (
          <>
            Search for malware that exploits any of the vulnerabilities in the
            list.
            <br />
            Example:
            <code>{EXPLOIT}[“CVE-2020-0601“, “CVE-2020-1472“]</code>
          </>
        )
      }
    }).getSuggestions()
  ],
  s => s.name
);

export const SUGGESTIONS_MAP = SEARCH_FIELD_SUGGESTIONS.reduce(
  (acc, suggestion) => {
    acc[suggestion.name] = suggestion;
    suggestion.aliases.forEach(alias => {
      acc[alias] = {
        ...suggestion,
        isAlias: true,
        name: alias
      };
    });
    return acc;
  },
  {}
);

export const searchFieldTrie = new Trie();

function constructSearchFieldTrie(suggestions) {
  suggestions.forEach(suggestion => {
    searchFieldTrie.add(suggestion.name, suggestion);

    if (suggestion.suggestions) {
      constructSearchFieldTrie(suggestion.suggestions);
    }
  });
}

constructSearchFieldTrie([
  ...SEARCH_FIELD_SUGGESTIONS,
  ...COMPARISON_SUGGESTIONS
]);

export function queryContainsSearchField(query) {
  return Object.values(SEARCH_FIELDS).some(keyword => query.includes(keyword));
}

export function getSuggestions(query) {
  let value = query;
  const openBracketIndex = query.lastIndexOf("[");
  const lastBracketIndex = query.lastIndexOf("]");
  if (openBracketIndex > -1 && lastBracketIndex < openBracketIndex) {
    value = query.substring(0, openBracketIndex + 1);
  }
  let result = searchFieldTrie.findAllOccurrences(value);
  if (!hasBalancedBrackets(query)) {
    result = result
      .map(suggestion => {
        if (suggestion.getSuggestion) {
          return {
            ...suggestion,
            ...suggestion.getSuggestion(query)
          };
        }
        return suggestion;
      })
      .filter(suggestion => {
        if (suggestion.filter) {
          return suggestion.filter(query);
        }
        return true;
      });
  }

  return sortBy(result, [
    // rank the suggestions that start with search value higher
    suggestion => !suggestion.name.startsWith(value),
    suggestion => suggestion.name.length
  ]);
}

export const ATTACK_TACTIC_SUGGESTIONS = [
  { name: "Initial Access", value: "TA0001" },
  { name: "Execution", value: "TA0002" },
  { name: "Persistence", value: "TA0003" },
  { name: "Privilege Escalation", value: "TA0004" },
  { name: "Defense Evasion", value: "TA0005" },
  { name: "Credential Access", value: "TA0006" },
  { name: "Discovery", value: "TA0007" },
  { name: "Lateral Movement", value: "TA0008" },
  { name: "Collection", value: "TA0009" },
  { name: "Exfiltration", value: "TA0010" },
  { name: "Command and Control", value: "TA0011" },
  { name: "Impact", value: "TA0040" }
];

export const ATTACK_TECHNIQUE_SUGGESTIONS = [
  { name: "Accessibility Features", value: "T1015" },
  { name: "Access Token Manipulation", value: "T1134" },
  { name: "Account Access Removal", value: "T1531" },
  { name: "Account Discovery", value: "T1087" },
  { name: "Account Manipulation", value: "T1098" },
  { name: "AppCert DLLs", value: "T1182" },
  { name: "AppInit DLLs", value: "T1103" },
  { name: "AppleScript", value: "T1155" },
  { name: "Application Access Token", value: "T1527" },
  { name: "Application Deployment Software", value: "T1017" },
  { name: "Application Shimming", value: "T1138" },
  { name: "Application Window Discovery", value: "T1010" },
  { name: "Audio Capture", value: "T1123" },
  { name: "Authentication Package", value: "T1131" },
  { name: "Automated Collection", value: "T1119" },
  { name: "Automated Exfiltration", value: "T1020" },
  { name: "Bash History", value: "T1139" },
  { name: ".bash_profile and .bashrc", value: "T1156" },
  { name: "Binary Padding", value: "T1009" },
  { name: "BITS Jobs", value: "T1197" },
  { name: "Bootkit", value: "T1067" },
  { name: "Browser Bookmark Discovery", value: "T1217" },
  { name: "Browser Extensions", value: "T1176" },
  { name: "Brute Force", value: "T1110" },
  { name: "Bypass User Account Control", value: "T1088" },
  { name: "Change Default File Association", value: "T1042" },
  { name: "Clear Command History", value: "T1146" },
  { name: "Clipboard Data", value: "T1115" },
  { name: "Cloud Instance Metadata API", value: "T1522" },
  { name: "Cloud Service Dashboard", value: "T1538" },
  { name: "Cloud Service Discovery", value: "T1526" },
  { name: "CMSTP", value: "T1191" },
  { name: "Code Signing", value: "T1116" },
  { name: "Command-Line Interface", value: "T1059" },
  { name: "Commonly Used Port", value: "T1043" },
  { name: "Communication Through Removable Media", value: "T1092" },
  { name: "Compile After Delivery", value: "T1500" },
  { name: "Compiled HTML File", value: "T1223" },
  { name: "Component Firmware", value: "T1109" },
  { name: "Component Object Model and Distributed COM", value: "T1175" },
  { name: "Component Object Model Hijacking", value: "T1122" },
  { name: "Connection Proxy", value: "T1090" },
  { name: "Control Panel Items", value: "T1196" },
  { name: "Create Account", value: "T1136" },
  { name: "Credential Dumping", value: "T1003" },
  { name: "Credentials from Web Browsers", value: "T1503" },
  { name: "Credentials in Files", value: "T1081" },
  { name: "Credentials in Registry", value: "T1214" },
  { name: "Custom Command and Control Protocol", value: "T1094" },
  { name: "Custom Cryptographic Protocol", value: "T1024" },
  { name: "Data Compressed", value: "T1002" },
  { name: "Data Destruction", value: "T1485" },
  { name: "Data Encoding", value: "T1132" },
  { name: "Data Encrypted for Impact", value: "T1486" },
  { name: "Data Encrypted", value: "T1022" },
  { name: "Data from Cloud Storage Object", value: "T1530" },
  { name: "Data from Information Repositories", value: "T1213" },
  { name: "Data from Local System", value: "T1005" },
  { name: "Data from Network Shared Drive", value: "T1039" },
  { name: "Data from Removable Media", value: "T1025" },
  { name: "Data Obfuscation", value: "T1001" },
  { name: "Data Staged", value: "T1074" },
  { name: "Data Transfer Size Limits", value: "T1030" },
  { name: "DCShadow", value: "T1207" },
  { name: "Defacement", value: "T1491" },
  { name: "Deobfuscate/Decode Files or Information", value: "T1140" },
  { name: "Disabling Security Tools", value: "T1089" },
  { name: "Disk Content Wipe", value: "T1488" },
  { name: "Disk Structure Wipe", value: "T1487" },
  { name: "DLL Search Order Hijacking", value: "T1038" },
  { name: "DLL Side-Loading", value: "T1073" },
  { name: "Domain Fronting", value: "T1172" },
  { name: "Domain Generation Algorithms", value: "T1483" },
  { name: "Domain Trust Discovery", value: "T1482" },
  { name: "Drive-by Compromise", value: "T1189" },
  { name: "Dylib Hijacking", value: "T1157" },
  { name: "Dynamic Data Exchange", value: "T1173" },
  { name: "Elevated Execution with Prompt", value: "T1514" },
  { name: "Email Collection", value: "T1114" },
  { name: "Emond", value: "T1519" },
  { name: "Endpoint Denial of Service", value: "T1499" },
  { name: "Execution Guardrails", value: "T1480" },
  { name: "Execution through API", value: "T1106" },
  { name: "Execution through Module Load", value: "T1129" },
  { name: "Exfiltration Over Alternative Protocol", value: "T1048" },
  { name: "Exfiltration Over Command and Control Channel", value: "T1041" },
  { name: "Exfiltration Over Other Network Medium", value: "T1011" },
  { name: "Exfiltration Over Physical Medium", value: "T1052" },
  { name: "Exploitation for Client Execution", value: "T1203" },
  { name: "Exploitation for Credential Access", value: "T1212" },
  { name: "Exploitation for Defense Evasion", value: "T1211" },
  { name: "Exploitation for Privilege Escalation", value: "T1068" },
  { name: "Exploitation of Remote Services", value: "T1210" },
  { name: "Exploit Public-Facing Application", value: "T1190" },
  { name: "External Remote Services", value: "T1133" },
  { name: "Extra Window Memory Injection", value: "T1181" },
  { name: "Fallback Channels", value: "T1008" },
  { name: "File and Directory Discovery", value: "T1083" },
  { name: "File and Directory Permissions Modification", value: "T1222" },
  { name: "File Deletion", value: "T1107" },
  { name: "File System Logical Offsets", value: "T1006" },
  { name: "File System Permissions Weakness", value: "T1044" },
  { name: "Firmware Corruption", value: "T1495" },
  { name: "Forced Authentication", value: "T1187" },
  { name: "Gatekeeper Bypass", value: "T1144" },
  { name: "Graphical User Interface", value: "T1061" },
  { name: "Group Policy Modification", value: "T1484" },
  { name: "Hardware Additions", value: "T1200" },
  { name: "Hidden Files and Directories", value: "T1158" },
  { name: "Hidden Users", value: "T1147" },
  { name: "Hidden Window", value: "T1143" },
  { name: "HISTCONTROL", value: "T1148" },
  { name: "Hooking", value: "T1179" },
  { name: "Hypervisor", value: "T1062" },
  { name: "Image File Execution Options Injection", value: "T1183" },
  { name: "Implant Container Image", value: "T1525" },
  { name: "Indicator Blocking", value: "T1054" },
  { name: "Indicator Removal from Tools", value: "T1066" },
  { name: "Indicator Removal on Host", value: "T1070" },
  { name: "Indirect Command Execution", value: "T1202" },
  { name: "Inhibit System Recovery", value: "T1490" },
  { name: "Input Capture", value: "T1056" },
  { name: "Input Prompt", value: "T1141" },
  { name: "Install Root Certificate", value: "T1130" },
  { name: "InstallUtil", value: "T1118" },
  { name: "Internal Spearphishing", value: "T1534" },
  { name: "Kerberoasting", value: "T1208" },
  { name: "Kernel Modules and Extensions", value: "T1215" },
  { name: "Keychain", value: "T1142" },
  { name: "Launch Agent", value: "T1159" },
  { name: "Launchctl", value: "T1152" },
  { name: "Launch Daemon", value: "T1160" },
  { name: "LC_LOAD_DYLIB Addition", value: "T1161" },
  { name: "LC_MAIN Hijacking", value: "T1149" },
  { name: "LLMNR/NBT-NS Poisoning and Relay", value: "T1171" },
  { name: "Local Job Scheduling", value: "T1168" },
  { name: "Login Item", value: "T1162" },
  { name: "Logon Scripts", value: "T1037" },
  { name: "LSASS Driver", value: "T1177" },
  { name: "Man in the Browser", value: "T1185" },
  { name: "Masquerading", value: "T1036" },
  { name: "Modify Existing Service", value: "T1031" },
  { name: "Modify Registry", value: "T1112" },
  { name: "Mshta", value: "T1170" },
  { name: "Multiband Communication", value: "T1026" },
  { name: "Multi-hop Proxy", value: "T1188" },
  { name: "Multilayer Encryption", value: "T1079" },
  { name: "Multi-Stage Channels", value: "T1104" },
  { name: "Netsh Helper DLL", value: "T1128" },
  { name: "Network Denial of Service", value: "T1498" },
  { name: "Network Service Scanning", value: "T1046" },
  { name: "Network Share Connection Removal", value: "T1126" },
  { name: "Network Share Discovery", value: "T1135" },
  { name: "Network Sniffing", value: "T1040" },
  { name: "New Service", value: "T1050" },
  { name: "NTFS File Attributes", value: "T1096" },
  { name: "Obfuscated Files or Information", value: "T1027" },
  { name: "Office Application Startup", value: "T1137" },
  { name: "Parent PID Spoofing", value: "T1502" },
  { name: "Pass the Hash", value: "T1075" },
  { name: "Pass the Ticket", value: "T1097" },
  { name: "Password Filter DLL", value: "T1174" },
  { name: "Password Policy Discovery", value: "T1201" },
  { name: "Path Interception", value: "T1034" },
  { name: "Peripheral Device Discovery", value: "T1120" },
  { name: "Permission Groups Discovery", value: "T1069" },
  { name: "Plist Modification", value: "T1150" },
  { name: "Port Knocking", value: "T1205" },
  { name: "Port Monitors", value: "T1013" },
  { name: "PowerShell Profile", value: "T1504" },
  { name: "PowerShell", value: "T1086" },
  { name: "Private Keys", value: "T1145" },
  { name: "Process Discovery", value: "T1057" },
  { name: "Process Doppelgänging", value: "T1186" },
  { name: "Process Hollowing", value: "T1093" },
  { name: "Process Injection", value: "T1055" },
  { name: "Query Registry", value: "T1012" },
  { name: "Rc.common", value: "T1163" },
  { name: "Redundant Access", value: "T1108" },
  { name: "Registry Run Keys / Startup Folder", value: "T1060" },
  { name: "Regsvcs/Regasm", value: "T1121" },
  { name: "Regsvr32", value: "T1117" },
  { name: "Remote Access Tools", value: "T1219" },
  { name: "Remote Desktop Protocol", value: "T1076" },
  { name: "Remote File Copy", value: "T1105" },
  { name: "Remote Services", value: "T1021" },
  { name: "Remote System Discovery", value: "T1018" },
  { name: "Re-opened Applications", value: "T1164" },
  { name: "Replication Through Removable Media", value: "T1091" },
  { name: "Resource Hijacking", value: "T1496" },
  { name: "Revert Cloud Instance", value: "T1536" },
  { name: "Rootkit", value: "T1014" },
  { name: "Rundll32", value: "T1085" },
  { name: "Runtime Data Manipulation", value: "T1494" },
  { name: "Scheduled Task", value: "T1053" },
  { name: "Scheduled Transfer", value: "T1029" },
  { name: "Screen Capture", value: "T1113" },
  { name: "Screensaver", value: "T1180" },
  { name: "Scripting", value: "T1064" },
  { name: "Securityd Memory", value: "T1167" },
  { name: "Security Software Discovery", value: "T1063" },
  { name: "Security Support Provider", value: "T1101" },
  { name: "Server Software Component", value: "T1505" },
  { name: "Service Execution", value: "T1035" },
  { name: "Service Registry Permissions Weakness", value: "T1058" },
  { name: "Service Stop", value: "T1489" },
  { name: "Setuid and Setgid", value: "T1166" },
  { name: "Shared Webroot", value: "T1051" },
  { name: "Shortcut Modification", value: "T1023" },
  { name: "SID-History Injection", value: "T1178" },
  { name: "Signed Binary Proxy Execution", value: "T1218" },
  { name: "Signed Script Proxy Execution", value: "T1216" },
  { name: "SIP and Trust Provider Hijacking", value: "T1198" },
  { name: "Software Discovery", value: "T1518" },
  { name: "Software Packing", value: "T1045" },
  { name: "Source", value: "T1153" },
  { name: "Space after Filename", value: "T1151" },
  { name: "Spearphishing Attachment", value: "T1193" },
  { name: "Spearphishing Link", value: "T1192" },
  { name: "Spearphishing via Service", value: "T1194" },
  { name: "SSH Hijacking", value: "T1184" },
  { name: "Standard Application Layer Protocol", value: "T1071" },
  { name: "Standard Cryptographic Protocol", value: "T1032" },
  { name: "Standard Non-Application Layer Protocol", value: "T1095" },
  { name: "Startup Items", value: "T1165" },
  { name: "Steal Application Access Token", value: "T1528" },
  { name: "Steal Web Session Cookie", value: "T1539" },
  { name: "Stored Data Manipulation", value: "T1492" },
  { name: "Sudo Caching", value: "T1206" },
  { name: "Sudo", value: "T1169" },
  { name: "Supply Chain Compromise", value: "T1195" },
  { name: "Systemd Service", value: "T1501" },
  { name: "System Firmware", value: "T1019" },
  { name: "System Information Discovery", value: "T1082" },
  { name: "System Network Configuration Discovery", value: "T1016" },
  { name: "System Network Connections Discovery", value: "T1049" },
  { name: "System Owner/User Discovery", value: "T1033" },
  { name: "System Service Discovery", value: "T1007" },
  { name: "System Shutdown/Reboot", value: "T1529" },
  { name: "System Time Discovery", value: "T1124" },
  { name: "Taint Shared Content", value: "T1080" },
  { name: "Template Injection", value: "T1221" },
  { name: "Third-party Software", value: "T1072" },
  { name: "Time Providers", value: "T1209" },
  { name: "Timestomp", value: "T1099" },
  { name: "Transfer Data to Cloud Account", value: "T1537" },
  { name: "Transmitted Data Manipulation", value: "T1493" },
  { name: "Trap", value: "T1154" },
  { name: "Trusted Developer Utilities", value: "T1127" },
  { name: "Trusted Relationship", value: "T1199" },
  { name: "Two-Factor Authentication Interception", value: "T1111" },
  { name: "Uncommonlyy Used Port", value: "T1065" },
  { name: "Unused/Unsupported Cloud Regions", value: "T1535" },
  { name: "User Execution", value: "T1204" },
  { name: "Valid Accounts", value: "T1078" },
  { name: "Video Capture", value: "T1125" },
  { name: "Virtualization/Sandbox Evasion", value: "T1497" },
  { name: "Web Service", value: "T1102" },
  { name: "Web Session Cookie", value: "T1506" },
  { name: "Web Shell", value: "T1100" },
  { name: "Windows Admin Shares", value: "T1077" },
  {
    name: "Windows Management Instrumentation Event Subscription",
    value: "T1084"
  },
  { name: "Windows Management Instrumentation", value: "T1047" },
  { name: "Windows Remote Management", value: "T1028" },
  { name: "Winlogon Helper DLL", value: "T1004" },
  { name: "XSL Script Processing", value: "T1220" }
];

const tcIndicatorsMappingsGroupedByValue = Object.entries(
  Indicators.mappings
).reduce((acc, [key, value]) => {
  if (!acc[value]) {
    acc[value] = [];
  }
  acc[value].push(key);
  return acc;
}, {});

export const TC_INDICATORS = Object.entries(
  tcIndicatorsMappingsGroupedByValue
).map(([key, value]) => ({
  name: `${key} (IDs: ${value.join(", ")})`,
  value: value.join(",")
}));
